import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import ReactDOM from 'react-dom'
import classNames from 'classnames'
import _ from 'lodash'
import dayjs from 'dayjs'
import {Input, message, Modal, Progress} from 'antd'
import {Formik} from 'formik'
import useTranslate from 'modules/local/useTranslate'
import Translate from 'modules/local/Translate'
import {LoginContext} from 'components/LoginContext'
import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import useAsync from 'modules/asyncCache/useAsync'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
// callApi (không phải useAsyncAction) - ngoại lệ upload file đã chốt (xem docs/coding-style/
// conventions.md, useAsyncAction/useAsync đi qua Redux, không phù hợp cho input File thô).
import {callApi} from 'modules/asyncCache/callApi'
import usePaginationHelper, {createPagerKey} from '../../hooks/usePaginationHelper'
import {useHistory} from 'modules/navigation/useRouter'
import {getRouteSearchParams, useRouteSearchParam} from 'envs/_current/history'
import {Link} from 'react-router-dom'
import {staticPaths} from 'routes/staticPaths'
import getLinkToDetail from 'helpers/getLinkToDetail'
import DrawDialog from 'components/drawer/dialog/DrawDialog'
import LookupField from 'components/LookupField'
import {ChipGroup, Field, FormActions, Section} from 'components/form/ModernForm'
import {
    familyBook_createFamilyBook_Api,
    familyBook_createMyFamilyBook_Api,
    familyBook_getMyFamilyBooks_Api,
    familyBook_getMyFamilyDashboard_Api,
    familyBook_getMyFamilyForests_Api,
    familyBook_importGedcom_Api,
    familyBookMember_getMine_Api,
} from 'apis/familyBook'
import {familyMoment_getReminderSettings_Api, familyMoment_updateReminderSettings_Api,} from 'apis/familyMomentNotice'
import {
    IoAdd,
    IoBulbOutline,
    IoCalendarOutline,
    IoChevronBack,
    IoChevronForward,
    IoClose,
    IoCloudUploadOutline,
    IoHomeOutline,
    IoLibraryOutline,
    IoPencilOutline,
    IoPeopleOutline,
    IoPersonCircleOutline,
    IoPrintOutline,
    IoRemove,
    IoSettingsOutline,
    IoSwapVerticalOutline,
    IoTrashOutline,
    IoWarningOutline,
} from 'react-icons/io5'
import ErrorBoundary from 'components/error/ErrorBoundary'
import {HiCheckCircle} from 'react-icons/hi'
import {FamilyIcon} from 'components/Svgs/FamilyIconSvg'
import {CatholicHouseholdSvg} from 'components/Svgs/CatholicHouseholdSvg'
import {GiFamilyTree} from 'react-icons/gi'
import {FaPlus, FaRegStar, FaStar} from 'react-icons/fa6'
import {LoadingOutlined} from '@ant-design/icons'
import {getSuggestionStars, toggleSuggestionStar} from '../functions/suggestionStars'
import {computeAge, getGivenNameInitial} from '../functions/memberDisplay'
import useDeleteFamilyBookAction from '../functions/useDeleteFamilyBookAction'
import {renderParishLookupOption} from '../functions/lookupOptionRenderers'
import GenderIcon from '../components/GenderIcon'
import PriestBadge from '../components/PriestBadge'
import ReligiousBadge from '../components/ReligiousBadge'
import EditFamilyBookModal from '../components/EditFamilyBookModal'
import PersonNameField from '../components/PersonNameField'
import PedigreeBox from '../components/PedigreeBox'
import SvgPedigreeTree from '../components/SvgPedigreeTree'
import {
    buildMemberUnits,
    coupleKeyOf,
    findRootUnits,
    findViewerAnchorNames,
    groupUnitsForRender,
    PEDIGREE_MAX_DEPTH
} from '../functions/pedigreeTree'
import MemberDetailModal, {
    ALL_SACRAMENT_TYPES,
    ContactField,
    InfoField,
    OtherBookChip,
    RelativesCard,
    SacramentSlot
} from '../components/MemberDetailModal'
import {InfoBanner, MOMENT_META, MOMENT_TYPES_ORDERED, MomentRow} from '../components/MomentRow'
import MomentsPanel from '../components/MomentsPanel'

// ─── Header ─────────────────────────────────────────────────────────────────

const FAMILY_SUGGESTIONS = [
    {key: 'anniversaries', icon: IoCalendarOutline, title: 'family_suggestion_anniversaries_title', description: 'family_suggestion_anniversaries_description', ready: true},
    {key: 'liturgical', icon: IoLibraryOutline, title: 'family_suggestion_liturgical_title', description: 'family_suggestion_liturgical_description', ready: false},
    {key: 'family_prayer', icon: IoPeopleOutline, title: 'family_suggestion_prayer_title', description: 'family_suggestion_prayer_description', ready: false},
    {key: 'export_pdf', icon: IoLibraryOutline, title: 'family_suggestion_export_title', description: 'family_suggestion_export_description', ready: true},
    {key: 'parish_link', icon: IoHomeOutline, title: 'family_suggestion_parish_title', description: 'family_suggestion_parish_description', ready: true},
    {key: 'family_fund', icon: IoPeopleOutline, title: 'family_suggestion_fund_title', description: 'family_suggestion_fund_description', ready: false},
    {key: 'album', icon: IoLibraryOutline, title: 'family_suggestion_album_title', description: 'family_suggestion_album_description', ready: false},
    {key: 'journal', icon: IoBulbOutline, title: 'family_suggestion_journal_title', description: 'family_suggestion_journal_description', ready: false},
]

// Tab "Setting" trong panel Gợi ý - loại kỷ niệm nào hiện trong "NHẮC LỊCH KỶ NIỆM SẮP ĐẾN" (Trang
// Chủ) + trang Kỷ niệm (MomentsPanel). Default (chưa từng lưu) lấy theo default_enabled BE trả về
// (sinh nhật/giỗ/bổn mạng) - CHỈ dùng để hiển thị chip nào tô sẵn lúc mới vào, giá trị thật luôn lấy
// từ enabled_types BE trả (đã tự fallback default nếu user chưa từng đổi, xem
// FamilyMomentReminderSetting::getEnabledTypes()).
const MomentReminderSettingsTab = ({t}) => {
    const login = useContext(LoginContext)
    const {response, isLoading} = useAsync({apiInfo: familyMoment_getReminderSettings_Api, deps: [login?.id]})
    const allTypes = _.get(response, 'data.all_types', [])
    const [enabledTypes, setEnabledTypes] = useState(null)

    useEffect(() => {
        const fetched = _.get(response, 'data.enabled_types')
        if (fetched) setEnabledTypes(fetched)
    }, [response])

    const {handleAsyncAction: doSave, loading: saving} = useAsyncAction({
        apiInfo: familyMoment_updateReminderSettings_Api,
        onSuccess: () => message.success(t('moment reminder settings saved')),
        onError: () => message.error(t('error occurred')),
    })

    if (!login) {
        return <p className="text-xs text-color-400 italic px-1">{t('login to view collection')}</p>
    }
    if (isLoading && enabledTypes === null) {
        return <div className="flex items-center justify-center py-8"><LoadingOutlined className="text-family-500"/></div>
    }

    const options = (allTypes.length ? allTypes.map((x) => x.type) : MOMENT_TYPES_ORDERED)
        .map((type) => ({label: MOMENT_META[type]?.label || type, value: type}))

    return (
        <div className="flex flex-col gap-3 p-0 lg:p-2">
            {/* Heading trong nội dung tab - tab label bên ngoài rút gọn thành "Cài Đặt" (t('settings')),
                mô tả đầy đủ "Cài Đặt Nhắc Lịch" chuyển vào đây làm heading thay vì nhét hết vào label
                nút tab (dễ vỡ layout khi cạnh tab "Gợi ý"). */}
            <span className="font-bold text-sm text-color-000 px-1">{t('moment reminder settings')}</span>
            <InfoBanner>{t('moment_reminder_settings_subtitle')}</InfoBanner>
            <ChipGroup multiple options={options} value={enabledTypes || []} onChange={setEnabledTypes}/>
            <button
                type="button"
                disabled={saving}
                // JSON.stringify - cùng lỗi FormData array-of-value đã gặp ở recipients
                // (SendMomentNoticeModal.js): request POST đi qua createFormData(), FormData.append
                // không serialize array, coerce qua toString() -> mất cấu trúc, BE nhận string thay
                // vì array. Xem docs/coding-style/conventions.md "FormData — array/object field phải
                // JSON.stringify".
                onClick={() => doSave({enabled_types: JSON.stringify(enabledTypes || [])})}
                className="self-end px-3 py-1.5 rounded-lg bg-family-600 hover:bg-family-700 text-white text-xs font-semibold cursor-pointer disabled:opacity-60">
                {t('save')}
            </button>
        </div>
    )
}

const SUGGESTIONS_PANEL_TABS = {LIST: 'list', SETTINGS: 'settings'}

// Panel "Cấu hình" — góc phải header, đúng pattern MissalV2SettingsModal (DrawDialog + variant="icon").
// 2 tab, Setting đứng TRƯỚC/mặc định (icon+title ngoài đã đổi thành gear = trọng tâm giờ là cấu
// hình, không còn là "gợi ý"): "Cài Đặt Nhắc Lịch" (loại kỷ niệm muốn theo dõi -
// MomentReminderSettingsTab, gắn CHUNG vào đây thay vì mở icon/DrawDialog riêng) và "Gợi ý" (list
// tĩnh teaser roadmap, ⭐ lưu preference nhẹ ở localStorage, không gọi API - đã chốt cùng user).
const FamilySuggestionsPanel = () => {
    const t = useTranslate()
    const [stars, setStars] = useState(() => getSuggestionStars())
    const [tab, setTab] = useState(SUGGESTIONS_PANEL_TABS.SETTINGS)

    const onToggleStar = (key) => setStars(toggleSuggestionStar(key))

    return (
        <DrawDialog
            width={480}
            wrapClassName="family-theme"
            title={
                <div className="flex flex-center gap-2">
                    <IoSettingsOutline size={16}/>
                    <span className="font-bold uppercase tracking-wide">{t('family_config_panel_title')}</span>
                </div>
            }
            footer={false}
            closable={true}
            component={
                <div
                    style={{width: 40, height: 40, background: 'var(--color-family-700)', flexShrink: 0}}
                    className="flex flex-center rounded-xl cursor-pointer hover:opacity-85">
                    {/* Trước là bulb (chỉ "gợi ý") - đổi qua gear vì panel giờ gánh cả tab Setting
                        (nhắc lịch kỷ niệm), gear là icon "cài đặt/tuỳ chọn thêm" quen thuộc hơn. Tab
                        "Gợi ý" bên trong vẫn giữ icon bulb riêng để phân biệt 2 tab. */}
                    <IoSettingsOutline size={18} className="text-white"/>
                </div>
            }>
            {(isToggle) => isToggle && (
                <div className="flex flex-col gap-3 p-0 lg:p-2">
                    <div className="p-1 bg-family-50 rounded-xl flex items-center gap-1">
                        {[
                            {key: SUGGESTIONS_PANEL_TABS.SETTINGS, icon: IoSettingsOutline, label: t('settings')},
                            {key: SUGGESTIONS_PANEL_TABS.LIST, icon: IoBulbOutline, label: t('family_suggestions_title')},
                        ].map(({key, icon: Icon, label}) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => setTab(key)}
                                style={{padding: '0.375rem 0.25rem', cursor: 'pointer'}}
                                className={classNames(
                                    'flex-1 flex flex-center gap-1 whitespace-nowrap font-semibold text-xs rounded-lg transition-all duration-300',
                                    tab === key ? 'background text-family-700' : 'bg-family-50 hover:bg-family-100 text-family-700'
                                )}>
                                <Icon size={14}/>
                                {label}
                            </button>
                        ))}
                    </div>

                    {tab === SUGGESTIONS_PANEL_TABS.LIST && (
                        <div className="flex flex-col gap-2">
                            <p className="text-xs text-color-400 italic px-1">
                                {t('family_suggestions_subtitle')}
                            </p>
                            {FAMILY_SUGGESTIONS.map((item) => {
                                const Icon = item.icon
                                const starred = stars.includes(item.key)
                                return (
                                    <div
                                        key={item.key}
                                        className="p-3 flex items-center gap-3 border border-color-50 rounded-xl">
                                        <div
                                            style={{width: 36, height: 36}}
                                            className="flex flex-center rounded-lg bg-family-50 flex-shrink-0">
                                            <Icon size={16} className="text-family-700"/>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5">
                                                <span className="font-bold text-sm text-color-000 truncate">
                                                    {t(item.title)}
                                                </span>
                                                {!item.ready && (
                                                    <span className="px-1.5 py-0.5 rounded text-2xs font-semibold bg-color-100 text-color-400 flex-shrink-0">
                                                        {t('coming soon')}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-color-400 leading-snug mt-0.5">
                                                {t(item.description)}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => onToggleStar(item.key)}
                                            className="flex-shrink-0 cursor-pointer bg-transparent border-0 p-1">
                                            {starred
                                                ? <FaStar size={16} className="text-family-500"/>
                                                : <FaRegStar size={16} className="text-color-300"/>}
                                        </button>
                                    </div>
                                )
                            })}
                        </div>
                    )}

                    {tab === SUGGESTIONS_PANEL_TABS.SETTINGS && <MomentReminderSettingsTab t={t}/>}
                </div>
            )}
        </DrawDialog>
    )
}

const FamilyBooksHeader = () => {
    const t = useTranslate()

    return (
        <div
            style={{background: 'linear-gradient(135deg, var(--color-family-50) 0%, var(--color-family-100) 100%)'}}
            className="border-b border-color-100">
            <div className="max-w-2xl mx-auto px-2 md:px-0 py-3 flex items-center gap-3">
                <div
                    style={{width: 72, height: 72, borderRadius: 16, flexShrink: 0, background: 'var(--color-family-700)'}}
                    className="flex flex-center">
                    <FamilyIcon size={34} className="text-white"/>
                </div>
                <div className="flex flex-col flex-1 min-w-0 truncate">
                    <span className="font-bold text-xl lg:text-2xl text-color-000 tracking-wide leading-tight">
                        Kristo Family
                    </span>
                    <span className="text-sm font-semibold text-color-200 tracking-wide leading-snug mt-0.5">
                        {t('family_books_title')}
                    </span>
                    <span className="text-xs text-color-400 tracking-wide leading-snug">
                        {t('family_books_subtitle')}
                    </span>
                </div>
                <FamilySuggestionsPanel/>
            </div>
        </div>
    )
}

// ─── Main tab bar (top-level, dưới header - thay bottom-nav mobile trong mockup) ──────────────

// Tab thứ 3 đổi từ "Hồ Sơ" (ProfileSection) thành "Kỷ Niệm" (MomentsPanel) - theo yêu cầu, xem hồ sơ
// cá nhân giờ qua DrawDialog khi bấm thẻ "Hồ Sơ Của Tôi" ở Trang Chủ (MyProfileCard), không còn
// chiếm 1 tab chính riêng.
const MAIN_TABS = {HOME: 'home', FAMILY: 'family', MOMENTS: 'moments'}

// Icon tab Trang Chủ đổi từ IoHomeOutline (nhà, chung chung) sang CatholicHouseholdSvg (icon ĐÃ DÙNG
// sẵn cho "Sổ Gia Đình Công Giáo" ở module CatholicHousehold - AppIcon.js) - đúng bản chất nội dung
// tab này (toàn bộ là thống kê/lối vào Sổ Gia Đình Công Giáo, không phải trang chủ chung chung), tái
// dùng ĐÚNG icon đã có thay vì tự vẽ icon mới cho cùng 1 khái niệm. Icon tab Gia Phả & Sổ đổi từ
// HolyFamilySvg (hình người) sang GiFamilyTree (sơ đồ cây - ĐÃ DÙNG sẵn xuyên suốt Cây Gia Phả/nút
// "Xem toàn bộ phả hệ" trong chính file này) - khớp đúng bản chất "phả hệ" hơn hình người chung chung.
// statUnitKey - tái dùng key dịch CÓ SẴN làm đơn vị đứng sau số (không tự chế key mới): 'members'
// (đã dùng ở FamilyBookListItem: "12 thành viên"), 'family_subtab_books' (đã là label sub-tab "Sổ
// Gia Đình" ngay bên dưới), 'family_tab_moments_short' (chính label_key của tab này). Không có đơn
// vị thì chỉ hiện số trơ, không biết đang đếm cái gì (phản hồi thực tế).
const MAIN_TAB_ITEMS = [
    {key: MAIN_TABS.HOME, icon: <CatholicHouseholdSvg size={15}/>, label_key: 'family_tab_home', statKey: 'total_members', statUnitKey: 'members'},
    {key: MAIN_TABS.FAMILY, icon: <GiFamilyTree size={15}/>, label_key: 'family_tab_family', statKey: 'total_family_books', statUnitKey: 'family_subtab_books'},
    {key: MAIN_TABS.MOMENTS, icon: <IoCalendarOutline size={14}/>, label_key: 'family_tab_moments_short', statKey: 'total_upcoming_moments', statUnitKey: 'family_tab_moments_short'},
]

// Số thống kê (thành viên/sổ/kỷ niệm) gắn thẳng vào tab tương ứng - TRƯỚC ĐÂY là 1 hàng StatTile
// riêng bên dưới thẻ "Hồ Sơ Của Tôi" trong HomeSection, nằm sát trên nút CTA lớn nên hay bị bấm nhầm
// (phản hồi thực tế). Gộp vào đây vừa giảm 1 block vừa rút ngắn đường nhìn số -> tab liên quan (khớp
// đúng ý mockup: mỗi số đi thẳng lên tab của nó). `stats` được fetch 1 lần ở root (FamilyBooks) và
// dùng chung cho cả badge này lẫn HomeSection, không gọi API trùng lặp.
const MainTabBar = ({tab, setTab, stats = {}}) => {
    const t = useTranslate()
    return (
        <div
            style={{background: 'rgba(180, 83, 9, 0.08)'}}
            className="m-1 md:m-2 p-1 rounded-2xl flex items-center gap-3 transition-all duration-300">
            {MAIN_TAB_ITEMS.map((item) => {
                const isActive = tab === item.key
                const count = stats[item.statKey]
                return (
                    <button
                        key={item.key}
                        type="button"
                        onClick={() => setTab(item.key)}
                        style={{padding: '0.375rem 0.5rem', cursor: 'pointer'}}
                        className={classNames(
                            'flex-1 flex flex-col items-center gap-0.5 font-semibold rounded-xl transition-all duration-300',
                            isActive ? 'bg-family-600 text-white' : 'bg-transparent hover:opacity-80 text-family-700'
                        )}>
                        <span className={classNames('transition-transform duration-150', isActive && 'scale-110')}>
                            {item.icon}
                        </span>
                        <span className="text-center tracking-wider leading-tight text-xs md:text-sm lg:text-base">
                            {t(item.label_key)}
                        </span>
                        {count != null && (
                            <span className={classNames(
                                'text-2xs leading-tight text-center',
                                isActive ? 'text-white text-opacity-80' : 'text-family-500'
                            )}>
                                <span className="font-bold">{count}</span> {t(item.statUnitKey)}
                            </span>
                        )}
                    </button>
                )
            })}
        </div>
    )
}

// ─── Sub-tab bar trong "Gia Phả & Sổ" (đúng pattern HymnsTabBar) ───────────────

const FAMILY_SUB_TABS = {BOOKS: 'books', MEMBERS: 'members', TREE: 'tree'}

const FAMILY_SUB_TAB_ITEMS = [
    {key: FAMILY_SUB_TABS.MEMBERS, icon: <IoPeopleOutline size={14}/>, label_key: 'family_subtab_members'},
    {key: FAMILY_SUB_TABS.BOOKS, icon: <IoLibraryOutline size={14}/>, label_key: 'family_subtab_books'},
    {key: FAMILY_SUB_TABS.TREE, icon: <GiFamilyTree size={14}/>, label_key: 'family_subtab_tree'},
]

// 3 nút chia đều flex-1 trên màn hình hẹp dễ làm nhãn dài ("Cây Gia Phả") bể dòng - ép
// whitespace-nowrap + thu gọn gap/padding/cỡ chữ để cả 3 luôn vừa 1 hàng thay vì tự xuống dòng.
const FamilySubTabBar = ({tab, setTab, t}) => (
    <div className="p-1 bg-family-50 rounded-xl flex items-center gap-1 transition-all duration-300">
        {FAMILY_SUB_TAB_ITEMS.map(({key, icon, label_key}) => {
            const isActive = tab === key
            return (
                <button
                    key={key}
                    type="button"
                    onClick={() => setTab(key)}
                    style={{padding: '0.375rem 0.25rem', cursor: 'pointer'}}
                    className={classNames(
                        'flex-1 flex flex-center gap-1 whitespace-nowrap font-semibold text-xs rounded-lg transition-all duration-300',
                        isActive ? 'background text-family-700' : 'bg-family-50 hover:bg-family-100 text-family-700'
                    )}>
                    {icon}
                    {t(label_key)}
                </button>
            )
        })}
    </div>
)

// ─── Sổ Gia Đình - list + Lập Sổ Mới modal ─────────────────────────────────

const FamilyBookListItem = ({item, onEdit, onDelete, t}) => {
    const headMember = _.get(item, 'head_member')
    const headSpouseMember = _.get(item, 'head_spouse_member')
    const totalMembers = _.get(item, 'total.members', 0)
    // Giáo xứ · Giáo hạt · Tổng giáo phận - breadcrumb đủ ngữ cảnh giáo hội của sổ (mới có
    // deanery_name/diocese_name snapshot trên family_books, xem thêm 062_add_deanery_to_family_books.sql)
    const parishBreadcrumb = [item.parish_name, item.deanery_name, item.diocese_name].filter(Boolean).join(' · ')

    return (
        <Link
            to={getLinkToDetail(item)}
            className="p-3 flex flex-col gap-1.5 background border border-color-50 shadow-sm hover:shadow-md rounded-xl no-underline transition-all duration-300">
            <div className="flex items-center justify-between gap-2">
                {item.source_idcode && (
                    <span className="px-1.5 py-0.5 rounded text-2xs font-semibold bg-family-50 text-family-700 tracking-wider">
                        {item.source_idcode}
                    </span>
                )}
                {parishBreadcrumb && (
                    <span className="text-2xs text-color-400 truncate flex-1">{parishBreadcrumb}</span>
                )}
                {/* Chỉ hiện khi item.edit=true (BE tính sẵn qua canEditFamilyBook - creator hoặc bất
                    kỳ thành viên nào) - stopPropagation để không kích hoạt navigate của Link cha */}
                {item.edit && (
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                onEdit && onEdit(item)
                            }}
                            className="p-1 rounded cursor-pointer text-color-300 hover:text-family-700 hover:bg-family-50"
                            title={t('edit')}>
                            <IoPencilOutline size={14}/>
                        </button>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                onDelete && onDelete(item)
                            }}
                            className="p-1 rounded cursor-pointer text-color-300 hover:text-rose-600 hover:bg-rose-50"
                            title={t('delete')}>
                            <IoTrashOutline size={14}/>
                        </button>
                    </div>
                )}
            </div>
            <span className="font-bold text-sm text-color-000 leading-snug">{item.title}</span>
            {(headMember || headSpouseMember) && (
                <div className="px-2.5 py-1.5 flex flex-col gap-0.5 bg-family-50 border border-family-100 rounded-lg">
                    <span className="text-2xs font-semibold text-color-300 uppercase tracking-wide">
                        {t('head of household and spouse')}
                    </span>
                    <span className="text-xs font-semibold text-color-000 leading-snug">
                        {[headMember?.name, headSpouseMember?.name].filter(Boolean).join(' & ')}
                    </span>
                </div>
            )}
            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                <span className="px-2 py-0.5 rounded-md text-2xs font-bold bg-green-50 text-green-600">
                    {totalMembers} {t('members')}
                </span>
                {item.eff_date && (
                    <span className="px-2 py-0.5 rounded-md text-2xs font-semibold bg-color-50 text-color-400">
                        {t('family book eff date')}: {dayjs(item.eff_date).format('DD/MM/YYYY')}
                    </span>
                )}
                {(item.matrimony?.groom?.eff_date || item.matrimony?.bride?.eff_date) && (
                    <span className="px-2 py-0.5 rounded-md text-2xs font-semibold bg-rose-50 text-rose-600">
                        {t('wedding date')}: {dayjs(item.matrimony.groom?.eff_date || item.matrimony.bride?.eff_date).format('DD/MM/YYYY')}
                    </span>
                )}
            </div>
        </Link>
    )
}

// Tách riêng khỏi CreateFamilyBookModal (Modal wrapper bên dưới LUÔN mounted, chỉ đổi `open` - xem
// FamilyBooksSubTab: <CreateFamilyBookModal visible={showCreate} .../> không bọc điều kiện `{showCreate && ...}`)
// - Formik phải sống trong 1 component được MOUNT MỚI mỗi lần mở modal thì mới tự reset đúng; khai
// trực tiếp trong CreateFamilyBookModal (không remount theo `visible`) sẽ giữ nguyên giá trị cũ qua
// các lần mở/đóng khác nhau, dù `destroyOnHidden` đã hủy xong <Formik> con.
//
// `owner_type`/`owner_id` (optional) - org tự khai trực tiếp qua nút "+ Lập sổ mới" ở Workspace
// (xem WorkspaceFamilyBook.js, dùng LẠI NGUYÊN form này thay vì tạo 1 bản riêng - cùng field
// Chồng/Vợ, chỉ khác API/owner). Có 2 giá trị này -> bỏ Section chọn giáo xứ (org đã có sẵn giáo xứ
// qua liên kết association_id, xem FamilyBookController::createFamilyBook()) + gọi
// familyBook_createFamilyBook_Api (status=1, không qua duyệt) thay vì familyBook_createMyFamilyBook_Api
// (sổ cá nhân, status=-1 chờ duyệt).
const CreateFamilyBookForm = ({owner_type, owner_id, onClose, onSuccess, t}) => {
    const isOrgMode = !!(owner_type && owner_id)
    const {handleAsyncAction: doCreate, loading} = useAsyncAction({
        apiInfo: isOrgMode ? familyBook_createFamilyBook_Api : familyBook_createMyFamilyBook_Api,
        onSuccess: (result, data) => {
            message.success(t('family book created'))
            onSuccess && onSuccess(_.get(data, 'response.data'))
        },
        onError: () => message.error(t('error occurred')),
    })

    const initialValues = {
        parish: null,
        husband_name: '',
        husband_birth_date: '',
        husband_member_user: null,
        husband_member_user_id: null,
        wife_name: '',
        wife_birth_date: '',
        wife_member_user: null,
        wife_member_user_id: null,
    }

    // Không còn field "Tên sổ"/"Giới tính" riêng - title tự generate ở BE từ tên Chồng/Vợ
    // (FamilyBook::syncTitleFromHeadSpouseNames(), đã chạy sẵn mỗi khi thêm/sửa member), giới tính
    // suy thẳng từ nhãn Chồng (gender=1)/Vợ (gender=2), không cần ChipGroup chọn tay hay đồng bộ
    // theo tài khoản lookup nữa (khác bản cũ - xem lịch sử component này trước khi có Chồng/Vợ).
    const validate = (values) => {
        const errors = {}
        if (!isOrgMode && !values.parish?.id) errors.parish = t('required field')
        if (!values.husband_name && !values.wife_name) {
            errors.husband_name = t('at least one spouse name is required')
            errors.wife_name = t('at least one spouse name is required')
        }
        return errors
    }

    return (
        <Formik
            initialValues={initialValues}
            validate={validate}
            validateOnMount
            onSubmit={(values) => {
                const payload = {
                    parish_id: isOrgMode ? undefined : values.parish?.id,
                    husband_name: values.husband_name || undefined,
                    husband_birth_date: values.husband_birth_date || undefined,
                    husband_member_user_id: values.husband_member_user_id || undefined,
                    wife_name: values.wife_name || undefined,
                    wife_birth_date: values.wife_birth_date || undefined,
                    wife_member_user_id: values.wife_member_user_id || undefined,
                }
                isOrgMode ? doCreate(payload, {type: owner_type, id: owner_id}) : doCreate(payload)
            }}>
            {({values, touched, errors, setFieldValue, setValues, handleSubmit, isSubmitting, isValid}) => (
                <form onSubmit={handleSubmit} className="flex flex-col gap-3 pt-2">
                    {!isOrgMode && (
                        <Section title={t('belong to parish')} subtitle={t('parish link needs confirmation note')} theme="highlight">
                            <Field required error={touched.parish && errors.parish}>
                                <LookupField
                                    name="family_book_parish"
                                    apiPath="/lookup/parish/get-by-types"
                                    placeholder={t('choose parish')}
                                    defaultValue={values.parish?.value}
                                    renderOption={renderParishLookupOption}
                                    handleChange={(e) => setFieldValue('parish', e.target)}
                                />
                            </Field>
                            {/* Vừa chọn xong 1 giáo xứ - nhắc rõ NGAY tại đây (không chỉ subtitle nhỏ
                                phía trên) 2 hệ quả: (1) sổ vào trạng thái chờ duyệt, (2) giáo xứ có
                                quyền khoá bí tích sau khi xác nhận - cùng nội dung banner dùng ở
                                EditFamilyBookModal khi đổi giáo xứ, tránh user bất ngờ về sau. */}
                            {values.parish?.id && (
                                <InfoBanner>{t('parish link pending sacraments lock note')}</InfoBanner>
                            )}
                        </Section>
                    )}

                    {/* Chồng/Vợ - dùng chung PersonNameField với AddRelativeModal (tên tự do sửa,
                        KHÔNG bị khoá theo tên tài khoản dù đã liên kết) + cho phép tra cứu/liên kết
                        tài khoản Kristo thật ngay lúc lập sổ. Ngày sinh gộp ngay dưới tên (cùng
                        Section, KHÔNG cùng hàng ngang - input type="date" đủ rộng dễ chồng đè trên
                        mobile) - đỡ phải kéo xuống 1 Section riêng như bản cũ. Có thể để trống 1
                        trong 2 (ca "tế nhị" - vợ/chồng đã goá/ly hôn/chưa rõ lúc lập sổ), validate
                        chỉ bắt ít nhất 1 bên có tên. */}
                    <Section title={t('husband')}>
                        <Field error={touched.husband_name && errors.husband_name}>
                            <PersonNameField
                                name={values.husband_name}
                                onNameChange={(v) => setFieldValue('husband_name', v)}
                                linkedUser={values.husband_member_user}
                                onLink={(userInfo) => {
                                    const {id, date_of_birth} = userInfo ?? {}
                                    setValues({
                                        ...values,
                                        husband_member_user: userInfo?.owner || null,
                                        husband_member_user_id: id || null,
                                        husband_birth_date: values.husband_birth_date || date_of_birth,
                                    })
                                }}
                                onUnlink={() => setValues({...values, husband_member_user: null, husband_member_user_id: null})}
                                placeholder={t('husband full name placeholder')}
                                t={t}
                            />
                        </Field>
                        <Field label={t('date of birth')}>
                            <Input
                                type="date"
                                value={values.husband_birth_date}
                                onChange={(e) => setFieldValue('husband_birth_date', e.target.value)}
                            />
                        </Field>
                    </Section>

                    <Section title={t('wife')} subtitle={t('at least one spouse name is required')}>
                        <Field error={touched.wife_name && errors.wife_name}>
                            <PersonNameField
                                name={values.wife_name}
                                onNameChange={(v) => setFieldValue('wife_name', v)}
                                linkedUser={values.wife_member_user}
                                onLink={(userInfo) => {
                                    const {id, date_of_birth} = userInfo ?? {}
                                    setValues({
                                        ...values,
                                        wife_member_user: userInfo?.owner || null,
                                        wife_member_user_id: id || null,
                                        wife_birth_date: values.wife_birth_date || date_of_birth,
                                    })
                                }}
                                onUnlink={() => setValues({...values, wife_member_user: null, wife_member_user_id: null})}
                                placeholder={t('wife full name placeholder')}
                                t={t}
                            />
                        </Field>
                        <Field label={t('date of birth')}>
                            <Input
                                type="date"
                                value={values.wife_birth_date}
                                onChange={(e) => setFieldValue('wife_birth_date', e.target.value)}
                            />
                        </Field>
                    </Section>

                    <FormActions
                        onCancel={onClose}
                        submitText="create new family book"
                        isSubmitting={isSubmitting || loading}
                        isValid={isValid}
                    />
                </form>
            )}
        </Formik>
    )
}

// Modal wrapper - LUÔN mounted (đổi `open` qua prop `visible`, xem FamilyBooksSubTab), destroyOnHidden
// tự hủy/tạo lại nội dung con mỗi lần đóng/mở. Nội dung form tách hẳn ra CreateFamilyBookForm (chỉ
// mount khi `visible` - xem giải thích ở đó) để state nội bộ (headGenderTouched, Formik) LUÔN fresh
// mỗi lần mở, không phụ thuộc đúng-sai vào hành vi unmount của `destroyOnHidden`.
export const CreateFamilyBookModal = ({owner_type, owner_id, visible, onClose, onSuccess, t}) => (
    <Modal
        width={420}
        className="custom-modal"
        rootClassName="family-theme"
        open={visible}
        title={<div className="text-center font-bold text-color-000 uppercase tracking-wide">{t('create new family book')}</div>}
        destroyOnHidden
        footer={null}
        onCancel={onClose}>
        {visible && <CreateFamilyBookForm owner_type={owner_type} owner_id={owner_id} onClose={onClose} onSuccess={onSuccess} t={t}/>}
    </Modal>
)

const FamilyBooksSubTab = ({t}) => {
    const login = useContext(LoginContext)
    const [showCreate, setShowCreate] = useState(false)
    const [editingItem, setEditingItem] = useState(null)
    const [refreshCount, setRefreshCount] = useState(0)
    const confirmDelete = useDeleteFamilyBookAction(t, () => setRefreshCount((c) => c + 1))

    // compact:true - tự tắt header ListFilter (search + advance filter + sort), chỉ lấy `handleSearch`/
    // `keyword` để tự dựng thanh tìm kiếm RIÊNG, gọn hơn header mặc định (không có nút "advance" thừa
    // vì màn này không có filter nâng cao nào - đúng convention Input.Search đơn giản đã dùng ở
    // MomentsPanel.js cùng module). handleSearch/keyword vẫn đi qua CHUNG cơ chế URL query
    // (`useRouteSearchParam('keyword')`) như header mặc định, không tự chế state song song - API
    // familyBook_getMyFamilyBooks_Api đã khai sẵn paramsFields `keyword`, BE lọc theo title/source_idcode.
    const [pager_widget, {handleSearch, keyword}] = usePaginationHelper({
        compact: true,
        withKey: createPagerKey([login?.id, 'my-family-books', refreshCount]),
        pagerInfo: {
            apiInfo: familyBook_getMyFamilyBooks_Api,
            renderItem: (item, i) => (
                <FamilyBookListItem
                    key={_.get(item, 'id', i)}
                    item={item}
                    onEdit={setEditingItem}
                    onDelete={confirmDelete}
                    t={t}
                />
            ),
            renderWrap: (children) => (
                <div className="flex flex-col gap-3">{children}</div>
            ),
            renderEmpty: () => (
                <div className="flex flex-col items-center justify-center py-8 text-color-400">
                    <IoLibraryOutline size={28} className="mb-2 opacity-30"/>
                    <span className="text-sm">{t('no family books found')}</span>
                </div>
            ),
        },
        dependencies: [refreshCount],
    })

    if (!login) {
        return (
            <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <FamilyIcon size={40} className="text-color-300 mb-3"/>
                <p className="text-sm text-color-400">{t('login to view collection')}</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
                <span className="font-bold text-xs text-color-400 uppercase tracking-widest">
                    {t('my family books')}
                </span>
                <button
                    type="button"
                    onClick={() => setShowCreate(true)}
                    className="px-3 py-1.5 flex items-center gap-1.5 rounded-lg bg-family-600 hover:bg-family-700 text-white text-xs font-semibold cursor-pointer">
                    <FaPlus size={11}/>
                    {t('create new family book')}
                </button>
            </div>
            <Input.Search
                allowClear
                defaultValue={keyword}
                onSearch={handleSearch}
                placeholder={t('search family book placeholder')}
            />
            {pager_widget}
            <CreateFamilyBookModal
                visible={showCreate}
                onClose={() => setShowCreate(false)}
                onSuccess={() => {
                    setShowCreate(false)
                    setRefreshCount((c) => c + 1)
                }}
                t={t}
            />
            {editingItem && (
                <EditFamilyBookModal
                    item={editingItem}
                    onClose={() => setEditingItem(null)}
                    onSuccess={() => {
                        setEditingItem(null)
                        setRefreshCount((c) => c + 1)
                    }}
                    t={t}
                />
            )}
        </div>
    )
}

// ─── Rừng gia phả — N cây độc lập (sổ chưa link/chưa match không gộp chung) ───
// dùng chung cho cả sub-tab Thành Viên và Cây Gia Phả (chọn rừng 1 lần, áp cho cả 2).

const useMyFamilyForests = () => {
    const login = useContext(LoginContext)
    const {response, isLoading} = useAsync({apiInfo: familyBook_getMyFamilyForests_Api, deps: [login?.id]})
    const forests = _.get(response, 'data.forests', [])
    return {forests, isLoading, login}
}

// buildMemberUnits/coupleKeyOf/groupUnitsForRender - tách sang functions/pedigreeTree.js (dùng chung
// với renderer SVG thử nghiệm, xem SvgPedigreeTree.js), import ở đầu file.

// Chip chọn cây (rừng) - luôn hiện đủ, xuống dòng khi không đủ chỗ thay vì cuộn ngang: cuộn ngang
// dễ làm mất chip khỏi tầm nhìn trên mobile, khó bấm chọn đúng cây muốn xem.
//
// Tên chip = GỐC THẬT của rừng (findRootUnits, cùng nguồn FullPedigreeModal dùng dựng cây) - TRƯỚC
// ĐÂY lấy forest.books[0].title (sổ đầu tiên theo thứ tự mảng BE trả về, KHÔNG liên quan gì tới ai
// là gốc thật) - sai ngay khi rừng nhiều sổ, hiện tên 1 người "lưng chừng" bất kỳ thay vì đúng gốc
// (bug thực tế đã gặp). Root >1 unit (hiếm, nhiều nhánh tổ tiên rời rạc) -> ghép tên unit đầu (đã
// sắp theo năm sinh sớm nhất) + đếm thêm, khớp cách "+N" đang hiện cho số sổ.
//
// Kèm thêm "neo" cá nhân hoá sau tên gốc (vd "LÊ Túc & LÊ Thị Hoán +33 (Lê Viết Dũng ↑)") khi gốc đó
// dẫn xuống 1 trong các cha/mẹ chồng/vợ trực tiếp của NGƯỜI ĐANG XEM (findViewerAnchorNames, xem giải
// thích đầy đủ ở đó) - phản hồi thực tế "leo lên nhiều đời không biết đó là ai, thấy tên ba/mẹ mình
// thì hiểu ngay nhánh nào". Không có neo khớp (gốc không dẫn tới cha/mẹ trực tiếp nào của viewer) thì
// không hiện thêm gì - không đoán bừa.
//
// Điều kiện khớp PHẢI là CON RUỘT (relationship_type CHILD, đọc từ book_relations) ở 1 sổ THUỘC
// forest đang xét - KHÔNG chỉ "có mặt trong forest" (quá lỏng, dính nhầm trường hợp neo chỉ là rể/dâu
// married-in ở 1 sổ khác trong CÙNG forest chứ không phải máu mủ của chính nhánh đó, vd ông Dũng chỉ
// là rể bên nhà bà Bát - bug thực tế đã gặp: nhánh Nguyễn Văn Bền hiện nhầm "(Lê Viết Dũng)" thay vì
// đúng ra phải là "(Nguyễn Thị Bát)", người thật sự máu mủ ở nhánh đó).
const ForestSelector = ({forests, selected, setSelected, t}) => {
    if (forests.length <= 1) return null
    const anchors = findViewerAnchorNames(forests)
    return (
        <div className="flex flex-wrap gap-2">
            {forests.map((forest, i) => {
                const forestMembers = forest.members || []
                const forestByKey = _.keyBy(forestMembers, 'dedup_key')
                const rootUnits = findRootUnits(forestMembers, forestByKey)
                const rootUnit = rootUnits[0]
                const primaryTitle = rootUnit ? rootUnit.map((p) => p.name).join(' & ') : _.get(forest, 'books.0.title')
                const extra = forest.book_ids.length - 1
                const anchor = anchors.find((a) => (a.book_relations || []).some(
                    (br) => br.relationship_type === 'CHILD' && forest.book_ids.includes(br.family_book_id)
                ))
                return (
                    <button
                        key={i}
                        type="button"
                        onClick={() => setSelected(i)}
                        className={classNames(
                            'px-3 py-1.5 flex-shrink-0 rounded-full text-xs font-semibold cursor-pointer transition-all',
                            i === selected ? 'bg-family-600 text-white' : 'bg-family-50 text-family-700'
                        )}>
                        {primaryTitle}{extra > 0 ? ` +${extra}` : ''}
                        {anchor && <span className="ml-1 font-normal opacity-70">{t('forest chip anchor', {name: anchor.name})}</span>}
                    </button>
                )
            })}
        </div>
    )
}

// Bấm cả hàng để xem chi tiết (DrawDialog, tái dùng MemberDetailModal) - đúng yêu cầu "chỗ nào
// member, click vào thì cũng view được detail". Tuổi tính bằng computeAge (đã mất thì tính tới
// death_date), avatar lấy chữ cái đầu TÊN (không phải HỌ) qua getGivenNameInitial.
//
// KHÔNG hiện relationship_type ở đây nữa - đây là danh sách đã GỘP (dedup) từ nhiều sổ liên kết,
// relationship_type vốn là quan hệ so với gia trưởng của TỪNG sổ riêng lẻ, gộp chung nhiều sổ vào 1
// list thì nhãn này gây hiểu nhầm (không rõ đang nói tới quan hệ ở sổ nào) - bỏ hẳn, chỉ giữ ngày
// sinh (vẫn đúng data, không mơ hồ theo sổ nào).
//
// Badge tuổi/"đã match n sổ" dời XUỐNG dòng dưới (căn theo dòng ngày sinh) thay vì đặt lệch sang
// cột riêng bên phải toàn hàng - tránh chồng/che lên tên khi tên dài trên màn hình hẹp (mobile).
// Tuổi đứng TRƯỚC ngày sinh, cả 2 canh trái cùng 1 nhóm (không dàn 2 đầu justify-between như trước -
// phản hồi thực tế "canh trái, đưa tuổi lên trước") - liên hệ nhanh (điện thoại/email, cùng convention
// MemberRow ở FamilyBookDetail.js).
//
// "Đã match N sổ" - nằm CUỐI dòng tên (dòng 1), đẩy sang phải qua ml-auto, KHÔNG dùng position:
// absolute + padding cố định như bản trước (đã gây bug thật: pr-* cố định trừ chỗ CỐ ĐỊNH bất kể tên
// dài hay ngắn, khiến tên bị truncate dư thừa "HOÀNG Đức T..." dù thừa chỗ hiển thị đủ). flex-shrink-0
// trên badge + truncate trên tên (span overflow:hidden tự có min-width:0 theo flexbox, không cần
// thêm min-w-0 riêng) khiến trình duyệt tự chia chỗ ĐÚNG THEO NỘI DUNG THẬT: tên chỉ bị cắt bớt khi
// THỰC SỰ không đủ chỗ (badge ngắn -> tên được nhiều chỗ hơn, không badge -> tên full width), luôn hiện
// đủ cả 2 dù tên dài/màn hình nhỏ mà không đoán trước 1 con số pixel nào. KHÔNG dùng biến thể Tailwind
// sm:/md: (đã bị purge khỏi bản build tĩnh của dự án, xem comment isSm ở EditFamilyBookModal.js).
//
// Icon sổ (rê chuột xem tên các sổ chứa bản ghi) ĐÃ BỎ - không click ra được gì (chỉ là tooltip), và
// "thuộc về ít nhất 1 sổ" là điều hiển nhiên với MỌI dòng trong list này, lặp lại không có ý nghĩa
// phân biệt (phản hồi thực tế) - xoá theo, không giữ lại icon vô nghĩa.
const DedupedMemberRow = ({item, onClick, t}) => {
    const age = computeAge(item.birth_date, item.death_date)
    return (
        <div
            onClick={onClick}
            className="p-3 flex items-center gap-3 background border border-color-50 hover:shadow-md rounded-xl cursor-pointer transition-all">
            <div style={{width: 36, height: 36}} className="flex flex-center rounded-full bg-family-100 text-family-700 font-bold flex-shrink-0">
                {getGivenNameInitial(item.name)}
            </div>
            <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                <div className="flex items-center gap-1.5">
                    {/* Tên thánh - mờ/nghiêng đứng TRƯỚC tên, cùng convention hiển thị patron_saint_name
                        đã dùng ở các nơi khác trong app (Course, Church...), KHÔNG tự chế cách mới. */}
                    {item.patron_saint_name && (
                        <span className="font-medium text-xs text-color-300 italic flex-shrink-0 truncate">
                            {item.patron_saint_name}
                        </span>
                    )}
                    <span className="font-semibold text-sm text-color-000 truncate">{item.name}</span>
                    <GenderIcon gender={item.gender} size={13} className="text-color-300 flex-shrink-0"/>
                    {/* Đã liên kết tài khoản thật (has_account, BE quét member_user_id qua toàn bộ
                        nhóm dedup) - chỉ hiện dấu tick khi CÓ, không có thì im lặng (đúng convention
                        đã dùng ở MemberDetailModal/AddRelativeModal - "chưa có" không cần nhắc riêng).
                        Có item.member_user (BE trả kèm) -> dấu tick trỏ THẲNG qua hồ sơ user đó (mở
                        tab mới, stopPropagation để không kích hoạt luôn onClick mở MemberDetailModal
                        của cả hàng) - đúng yêu cầu "member có user thì link trỏ qua user đó". */}
                    {item.has_account && (
                        item.member_user ? (
                            <Link
                                to={getLinkToDetail(item.member_user)}
                                target="_blank"
                                onClick={(e) => e.stopPropagation()}
                                title={t('linked account')}
                                className="flex-shrink-0 flex items-center">
                                <HiCheckCircle size={13} className="text-green-600"/>
                            </Link>
                        ) : (
                            <span title={t('linked account')} className="flex-shrink-0 flex items-center">
                                <HiCheckCircle size={13} className="text-green-600"/>
                            </span>
                        )
                    )}
                    {item.is_priest && <PriestBadge small/>}
                    {item.is_religious && <ReligiousBadge small/>}
                    {item.appearances > 1 && (
                        <span
                            onClick={(e) => e.stopPropagation()}
                            className="ml-auto flex-shrink-0 pl-1.5 text-2xs font-semibold text-green-600 whitespace-nowrap">
                            {t('matched across n books', {n: item.appearances})}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {/* Ngày sinh TRƯỚC, tuổi SAU - đảo lại theo góp ý mới nhất (trước đó đã từng đổi
                        ngược lại theo phản hồi cũ "đưa tuổi lên trước" - chốt lại lần này ngày sinh
                        trước, đúng thứ tự tự nhiên "sinh ngày X, Y tuổi", khớp thứ tự đang dùng ở
                        MemberRow/FamilyBookDetail.js. */}
                    <span className="text-xs text-color-400 truncate">
                        {item.birth_date ? dayjs(item.birth_date).format('DD/MM/YYYY') : t('not provided yet')}
                    </span>
                    {age != null && (
                        <span className="px-2 py-0.5 rounded-full text-2xs font-bold bg-family-50 text-family-700 whitespace-no-wrap flex-shrink-0">
                            {age} {t('years old')}{item.death_date ? ' ✝' : ''}
                        </span>
                    )}
                </div>
                {/* Liên hệ - DÒNG RIÊNG (không nhét icon nhỏ chung dòng tuổi/ngày sinh nữa - dễ bị che/
                    lẫn khi hàng có nhiều thông tin), hiện thẳng số đt/email dạng chữ bấm gọi/mail được
                    ngay, ĐÚNG convention đã dùng ở MemberRow (FamilyBookDetail.js) - không tự chế cách
                    hiển thị song song. item.contact_phone/contact_email đã được BE lọc theo quyền
                    (canManageFamilyBookContact() - xem getMyFamilyForests()), rỗng nghĩa là KHÔNG có
                    quyền xem hoặc chưa khai, cả 2 case đều ẩn dòng này. */}
                {(item.contact_phone || item.contact_email) && (
                    <div
                        className="flex items-center flex-wrap gap-x-2 gap-y-0.5 text-2xs"
                        onClick={(e) => e.stopPropagation()}>
                        {item.contact_phone && (
                            <a href={`tel:${item.contact_phone}`} className="text-family-700 hover:underline whitespace-nowrap">
                                {item.contact_phone}
                            </a>
                        )}
                        {item.contact_email && (
                            <a href={`mailto:${item.contact_email}`} className="text-family-700 hover:underline truncate">
                                {item.contact_email}
                            </a>
                        )}
                    </div>
                )}
            </div>
            <IoChevronForward size={16} className="text-color-300 flex-shrink-0"/>
        </div>
    )
}

const FamilyMembersSubTab = ({t}) => {
    const {forests, isLoading, login} = useMyFamilyForests()
    const [selected, setSelected] = useState(0)
    const [detailMemberId, setDetailMemberId] = useState(null)
    const [keyword, setKeyword] = useState('')
    const forest = forests[selected]

    const byKey = useMemo(() => _.keyBy(forest?.members || [], 'dedup_key'), [forest])
    // sắp theo năm sinh (lớn tuổi nhất trước) + vợ/chồng đứng liền kề nhau, thay vì thứ tự thô từ BE.
    // mergeSpouses=true - list PHẲNG từng người (DedupedMemberRow, 1 dòng/người), không phải sơ đồ
    // phân nhánh - 1 người nhiều vợ/chồng chỉ nên xuất hiện ĐÚNG 1 lần (mỗi vợ/chồng vẫn ra dòng
    // riêng của chính họ - flat() không gộp mất họ, chỉ tránh trùng lặp chính primary).
    const orderedMembers = useMemo(() => buildMemberUnits(forest?.members || [], byKey, undefined, true).flat(), [forest, byKey])

    // Tìm theo tên (kèm tên thánh) trong danh sách ĐÃ GỘP hiện có - toàn bộ forest.members đã tải sẵn
    // 1 lần (useMyFamilyForests, không phân trang) nên lọc thẳng phía FE, không gọi thêm API. Khớp
    // convention Input.Search rời (không qua usePaginationHelper - danh sách này không phân trang) đã
    // dùng ở MomentsPanel.js cùng module.
    const filteredMembers = useMemo(() => {
        const kw = keyword.trim().toLowerCase()
        if (!kw) return orderedMembers
        return orderedMembers.filter((m) =>
            [m.name, m.patron_saint_name].filter(Boolean).some((s) => s.toLowerCase().includes(kw))
        )
    }, [orderedMembers, keyword])

    if (!login) {
        return (
            <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <IoPeopleOutline size={40} className="text-color-300 mb-3"/>
                <p className="text-sm text-color-400">{t('login to view collection')}</p>
            </div>
        )
    }
    if (isLoading && !forests.length) {
        return <div className="flex items-center justify-center py-8"><LoadingOutlined className="text-family-500"/></div>
    }
    if (!forests.length) {
        return (
            <div className="flex flex-col items-center justify-center py-8 text-color-400">
                <IoPeopleOutline size={28} className="mb-2 opacity-30"/>
                <span className="text-sm">{t('no family books found')}</span>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-3">
            <ForestSelector forests={forests} selected={selected} setSelected={setSelected} t={t}/>
            <span className="text-xs text-color-400">
                {t('n members across n books', {members: forest.total_members, books: forest.book_ids.length})}
            </span>
            <Input.Search
                allowClear
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder={t('search by member name')}
            />
            {keyword.trim() && !filteredMembers.length ? (
                <div className="flex flex-col items-center justify-center py-8 text-color-400">
                    <IoPeopleOutline size={28} className="mb-2 opacity-30"/>
                    <span className="text-sm">{t('no results found')}</span>
                </div>
            ) : (
                <div className="flex flex-col gap-2">
                    {filteredMembers.map((m) => <DedupedMemberRow key={m.id} item={m} onClick={() => setDetailMemberId(m.id)} t={t}/>)}
                </div>
            )}
            <div className="h-16"/>
            {detailMemberId && (
                <MemberDetailModal key={detailMemberId} memberId={detailMemberId} onClose={() => setDetailMemberId(null)}/>
            )}
        </div>
    )
}

// ─── Cây Gia Phả kiểu "Tâm điểm" (Focus) ───────────────────────────────────
// Thay hẳn cho cách hiển thị cũ "list phẳng theo thế hệ tuyệt đối, bấm để drill-down" - kiểu đó
// không thể hiện được quan hệ cha/mẹ - con khi liên kết CHỈ suy được qua relationship_type (Thêm
// Thân Nhân chưa thu thập father_member_id/mother_member_id) chứ không phải blood-chain thật, nên
// hasChildren/children tính theo blood-chain bị bỏ sót → hiển thị con lạc khỏi cha mẹ.
// Giờ luôn có 1 "tâm điểm" (mặc định = gia trưởng sổ đầu tiên), hiển thị 4 tầng quanh họ: Ông Bà /
// Cha Mẹ / [Tâm điểm + Vợ-Chồng] / Các con - đúng chuẩn app gia phả (Ancestry, MyHeritage), tự tính
// lại theo effective_parent_dedup_keys (BE đã gộp blood-chain thật + suy theo relationship_type,
// xem FamilyBookController::getMyFamilyForests). Bấm vào bất kỳ ai (trừ chính tâm điểm) -> người đó
// thành tâm điểm mới. "Anh chị em cùng thế hệ" (chung effective parent) hiện thành chip chọn nhanh.

const RELATIVE_STYLE = {bg: 'bg-family-50', border: 'border-family-200'}

// Bề rộng cố định 1 card - dùng flex-wrap + justify-center (KHÔNG dùng CSS Grid cột cố định nữa):
// grid cột cố định khi số người < số cột sẽ dồn hết sang trái, để trống bên phải trông lệch/mất cân
// đối - flex-wrap + justify-center tự căn giữa đúng theo số người thực tế có trong tầng.
const TREE_CARD_WIDTH = 152
// Bề rộng THU NHỎ trên màn hẹp (isSm) - riêng khối "nhiều vợ/chồng" (SiblingMultiMarriageUnit) là 1
// hàng flex KHÔNG tự xuống dòng được (phải giữ nguyên 1 hàng để giữ ý nghĩa gạch nối vợ/chồng liền
// primary), 3 ô x 152px + gạch nối ~480px - VƯỢT khả dụng của màn hình hẹp (iPhone X ~351px sau khi
// trừ padding), bị tràn ngang/cắt 2 đầu - phản hồi thực tế đã gặp. Thu bề rộng ô (152 -> 108) đưa
// tổng về ~330px (kèm khoảng chừa an toàn - container ngoài vẫn có overflow-x-auto làm lưới an toàn
// cho phần dư nhỏ nếu tên người dài hơn bình thường), vừa lọt màn hẹp mà không phải đổi bố cục ngang
// đang dùng.
const TREE_CARD_WIDTH_SM = 100

// Icon giới tính đặt Ở HÀNG RIÊNG bên trên tên (KHÔNG absolute) - absolute + text-center không đoán
// trước được điểm cuối dòng chữ để né, tên dài is bị icon đè lên nhiều lần dù đã thêm padding. Theo
// flow bình thường thì tuyệt đối không thể chồng lên nhau nữa, bất kể tên dài ngắn thế nào.
// Bấm CẢ CARD -> chọn làm tâm điểm mới (hành động chính, không áp dụng khi đây chính là tâm điểm -
// `focus=true`); bấm riêng TÊN -> xem hồ sơ đầy đủ. Dùng chung cho mọi tầng (ông bà/cha mẹ/anh chị
// em/con) VÀ tâm điểm (qua prop `focus`) - trước đây tâm điểm có component riêng, dễ lệch style.
// inLaw: true khi đây là vợ/chồng "dâu/rể" của 1 người MÁU MỦ trong tầng (ghép vào qua
// buildMemberUnits, bản thân họ không nằm trong danh sách máu mủ của tầng này) - CHỈ có ý nghĩa ở
// tầng con/anh-chị-em (tầng ông bà/cha mẹ ghép 2 người đều là máu mủ với tâm điểm, không phải dâu
// rể) - xem UnitCard. Đánh dấu bằng CẢ 2: viền nét đứt (khác viền liền của máu mủ) + nhãn "Dâu"/"Rể"
// thay cho "Con trai"/"Con gái" - tránh nhầm dâu/rể là con ruột khi lướt nhanh qua cây (đã gặp thực
// tế: 1 tầng "6 người con" thực ra là 12 người vì có 6 dâu/rể trộn chung, không phân biệt được).
const PersonBlock = ({person, tier, focus, inLaw, onSelect, onViewDetail, t}) => {
    const {isSm} = useContext(LayoutContext)
    const age = computeAge(person.birth_date, person.death_date)
    const roleLabel = inLaw
        ? (person.gender === 2 ? t('daughter in law') : person.gender === 1 ? t('son in law') : t('relative by marriage'))
        : tier === 'parent' ? (person.gender === 2 ? t('mother') : t('father'))
        : tier === 'child' ? (person.gender === 2 ? t('daughter') : t('son'))
        : null
    // Đã mất -> kèm dấu ✝ ngay sau tuổi (tuổi lúc mất) - quy ước gia phả chuẩn.
    const ageLabel = age != null ? `${age} ${t('years old')}${person.death_date ? ' ✝' : ''}` : null
    const caption = [ageLabel, roleLabel].filter(Boolean).join(' · ')

    return (
        <div
            onClick={() => !focus && onSelect(person.dedup_key)}
            style={{width: isSm ? TREE_CARD_WIDTH_SM : TREE_CARD_WIDTH}}
            className={classNames(
                // h-full + justify-center - để cùng hàng flex (items-stretch, xem FamilyTreeSubTab)
                // co giãn đều cao bằng nhau bất kể có dòng caption (tuổi/vai trò) hay không (người
                // không có ngày sinh/vai trò thiếu hẳn 1 dòng, box ngắn hơn hẳn box cạnh bên - phản
                // hồi thực tế "cho có cùng chiều cao") - nội dung tự canh giữa theo chiều dọc trong
                // khung đã giãn đều, không lệch lên đỉnh để trống khoảng trắng phía dưới.
                'flex-shrink-0 h-full p-3 flex flex-col items-center justify-center gap-0.5 text-center rounded-xl transition-all',
                focus
                    ? 'bg-family-600'
                    : classNames(
                        inLaw ? 'border border-dashed' : 'border',
                        'cursor-pointer hover:shadow-md',
                        RELATIVE_STYLE.bg, RELATIVE_STYLE.border
                    )
            )}>
            {/* Tên trước, icon giới tính SAU (đồng nhất với các nơi khác) - vẫn ở hàng riêng (không
                cùng dòng với tên) để không tái phát bug icon đè chữ khi tên dài + text-center.
                w-full - PersonBlock là flex-col + items-center (không phải items-stretch), con bên
                trong mặc định co theo ĐÚNG độ dài nội dung (shrink-to-fit) chứ KHÔNG tự lấy hết bề
                rộng ô cha - tên dài (vd "Joseph Mai Ngọc Thành") không có biên rõ ràng để ngắt dòng,
                đẩy min-content width vượt quá bề rộng cố định của ô, kéo CẢ Ô phình to hơn hẳn ô cạnh
                bên (phản hồi thực tế "để responsive được không" - đúng lỗi tràn/phình ô đã gặp nhiều
                lần với flex, xem MemberCompareCard). w-full ép tên LUÔN đúng bằng bề rộng ô (đã cố
                định qua TREE_CARD_WIDTH), buộc ngắt dòng tại đó thay vì phình ô. Kèm max-lines-3 chặn
                trần chiều cao khi tên quá dài (cắt bớt + "…", xem đủ tên qua title/bấm xem chi tiết). */}
            <span
                onClick={(e) => { e.stopPropagation(); onViewDetail(person.id) }}
                className={classNames('w-full max-lines-3 font-bold text-sm leading-snug cursor-pointer hover:underline', focus ? 'text-white' : 'text-color-000')}
                title={person.name}>
                {person.name}
            </span>
            <GenderIcon gender={person.gender} size={13} className={focus ? 'text-white opacity-80' : 'text-family-400'}/>
            {caption && (
                <span className={classNames('text-2xs font-semibold', focus ? 'text-family-100' : inLaw ? 'text-color-400 italic' : 'text-family-600')}>{caption}</span>
            )}
            {/* Trong cây (node nhỏ, khác list/detail vốn cố ý hiện CẢ 2 badge - xem ReligiousBadge.js)
                chỉ hiện 1 nhãn: đã Truyền Chức (linh mục) thì thôi không hiện thêm Tu Sĩ - "cao hơn"
                tu sĩ nên nhãn linh mục đã bao hàm ý đang sống đời thánh hiến, không cần 2 badge chồng
                nhau trên khung hẹp. Chưa truyền chức nhưng có ơn gọi thánh hiến -> vẫn hiện Tu Sĩ. */}
            {person.is_priest ? <PriestBadge className="mt-0.5"/> : person.is_religious && <ReligiousBadge className="mt-0.5"/>}
        </div>
    )
}

// Vạch nối ngang ngắn giữa 2 người trong 1 cặp vợ/chồng - "2 người là vợ chồng, nối lại với nhau".
const UnitConnector = () => <div className="flex-shrink-0 self-center w-2 h-0.5 rounded-full bg-family-300"/>

// Tầng nào coi vế thứ 2 trong unit (spouse ghép vào qua buildMemberUnits) là "dâu/rể" - CHỈ tầng
// con/anh-chị-em, vì `people` truyền vào TierGroup ở 2 tầng này là danh sách MÁU MỦ thật (con ruột/
// anh chị em ruột), spouse ghép vào chắc chắn là người ngoài máu mủ (dâu/rể). Tầng ông bà/cha mẹ thì
// unit ghép 2 người ĐỀU là máu mủ (cha VÀ mẹ), không phải quan hệ dâu/rể. "pedigree" - từng hàng
// trong FullPedigreeModal (nhóm theo generation) cũng là danh sách máu mủ thật ở đúng thế hệ đó,
// cùng bản chất với "child"/"sibling".
const IN_LAW_TIERS = new Set(['child', 'sibling', 'pedigree'])

// 1 người đơn hoặc 1 cặp vợ/chồng (ghép qua buildMemberUnits, cùng cơ chế với sub-tab Thành Viên) -
// "nếu đã có gia đình/sổ riêng thì đưa cặp vợ chồng vào đây" áp dụng ĐỒNG NHẤT mọi tầng (ông bà/cha
// mẹ/anh chị em/con), không chỉ riêng tâm điểm như bản trước.
//
// unit.length > 2 (buildMemberUnits gọi với mergeSpouses=true - 1 người NHIỀU vợ/chồng gộp chung 1
// unit) - đây là khu vực LIỆT KÊ PHẲNG, không có phân nhánh trực quan như Cây Gia Phả, nên lặp lại
// nguyên khối PersonBlock cho từng vợ/chồng sẽ trông như trùng lặp/bug. Theo đúng cách các nền tảng
// phả hệ lớn làm ở chế độ "Family view" tóm tắt (Ancestry/FamilySearch) - người chính hiện 1 lần,
// vợ/chồng liệt kê thành DANH SÁCH DỌC bên dưới (mỗi người 1 dòng riêng, bấm để xem/chuyển tâm điểm
// sang đúng người đó) - KHÔNG nhồi chung 1 dòng ngang rồi cắt chữ (thử rồi, không đọc được tên).
const UnitCard = ({unit, tier, focus, onSelect, onViewDetail, t}) => {
    if (unit.length > 2) {
        const [primary, ...spouses] = unit
        return (
            <div className={classNames('flex flex-col items-center flex-shrink-0', focus && 'rounded-2xl border-2 border-family-600 p-1')}>
                <PersonBlock person={primary} tier={tier} focus={focus} onSelect={onSelect} onViewDetail={onViewDetail} t={t}/>
                <div style={{width: TREE_CARD_WIDTH}} className="mt-1 flex flex-col gap-1">
                    {spouses.map((s) => (
                        // Nền/viền/chữ CỐ ĐỊNH (không đổi theo `focus` như PersonBlock) - khối này
                        // đứng NGOÀI PersonBlock nên không có nền cam phía sau dù focus=true, dùng
                        // chữ nhạt (text-family-100) theo điều kiện focus trước đây làm mất hẳn chữ
                        // trên nền trắng (bug thực tế "đọc ko được").
                        <div
                            key={s.dedup_key}
                            onClick={() => onSelect(s.dedup_key)}
                            className="px-1.5 py-1 rounded-lg border border-dashed border-family-200 bg-family-50 text-2xs text-family-700 text-center leading-snug cursor-pointer hover:shadow-sm">
                            {s.name}
                        </div>
                    ))}
                </div>
            </div>
        )
    }
    return (
        <div className={classNames('flex items-stretch gap-1 flex-shrink-0', focus && 'rounded-2xl border-2 border-family-600 p-1')}>
            {unit.map((p, i) => (
                <React.Fragment key={p.dedup_key}>
                    {i > 0 && <UnitConnector/>}
                    <PersonBlock
                        person={p}
                        tier={tier}
                        focus={focus}
                        inLaw={i > 0 && IN_LAW_TIERS.has(tier)}
                        onSelect={onSelect}
                        onViewDetail={onViewDetail}
                        t={t}
                    />
                </React.Fragment>
            ))}
        </div>
    )
}

// Vạch nối centered giữa 2 tầng - chấm tròn 2 đầu để rõ đây LÀ 1 trục nối liền các tầng.
const TierConnector = () => (
    <div className="self-center flex flex-col items-center">
        <div className="w-1.5 h-1.5 rounded-full bg-family-300"/>
        <div className="w-0.5 h-5 bg-family-300"/>
        <div className="w-1.5 h-1.5 rounded-full bg-family-300"/>
    </div>
)

// Nhiều vợ/chồng (units.length > 1, gom qua groupUnitsForRender) ở tầng "cùng thế hệ" Tâm Điểm -
// primary hiện 1 LẦN duy nhất, vợ/chồng xếp 2 bên nối bằng UnitConnector (PersonBlock/TREE_CARD_WIDTH
// - RIÊNG cho khu vực liệt kê phẳng này, khác cách Cây Gia Phả đang làm - xem PedigreeNode) - TRƯỚC
// ĐÂY tách N UnitCard cạnh nhau (trông như N người khác tên trùng, đúng bug thực tế đã gặp: "Mai Văn
// Phần là 2 ông khác nhau"), giờ gộp về ĐÚNG 1 khối theo yêu cầu thực tế ("gom thành 1 khối").
//
// Con của TỪNG cuộc hôn nhân KHÔNG còn nằm lồng trong khối này nữa - dời hẳn sang 1 TẦNG CON riêng
// bên dưới TOÀN BỘ hàng anh chị em (xem khối "children tier" ở FamilyTreeSubTab) để những anh/chị/em
// không có con hiển thị ở đây (Hai, Lục) đứng NGANG HÀNG với khối này, không bị đẩy lạc xuống dưới do
// khối nhiều vợ/chồng + nhiều con cao hơn hẳn (flex-wrap tự xuống dòng theo chiều cao dòng trước đó -
// phản hồi thực tế đã gặp "Hai/Lục chen giữa/lạc xuống dưới").
const SiblingMultiMarriageUnit = ({primary, units, focusKey, onSelect, onViewDetail, t}) => {
    const isFocus = primary.dedup_key === focusKey
    const spouses = units.map((u) => u[1]).filter(Boolean)
    const mid = Math.ceil(spouses.length / 2)
    const leftSpouses = spouses.slice(0, mid)
    const rightSpouses = spouses.slice(mid)
    return (
        <div className={classNames('flex items-stretch gap-1', isFocus && 'rounded-2xl border-2 border-family-600 p-1')}>
            {leftSpouses.map((s) => (
                <React.Fragment key={s.dedup_key}>
                    <PersonBlock person={s} tier="sibling" inLaw onSelect={onSelect} onViewDetail={onViewDetail} t={t}/>
                    <UnitConnector/>
                </React.Fragment>
            ))}
            <PersonBlock person={primary} tier="sibling" focus={isFocus} onSelect={onSelect} onViewDetail={onViewDetail} t={t}/>
            {rightSpouses.map((s) => (
                <React.Fragment key={s.dedup_key}>
                    <UnitConnector/>
                    <PersonBlock person={s} tier="sibling" inLaw onSelect={onSelect} onViewDetail={onViewDetail} t={t}/>
                </React.Fragment>
            ))}
        </div>
    )
}

// `focusKey` (optional) - unit nào chứa người này được tô nổi bật kiểu "tâm điểm" (dùng cho chính
// tầng đang chứa tâm điểm, vd tầng "anh chị em cùng thế hệ").
const TierGroup = ({label, people, tier, byKey, focusKey, onSelect, onViewDetail, t}) => {
    if (!people.length) return null
    // mergeSpouses=true - đây là hàng thẻ PHẲNG (không phân nhánh trực quan như Cây Gia Phả), 1
    // người nhiều vợ/chồng gộp về đúng 1 thẻ (xem giải thích ở buildMemberUnits/UnitCard).
    const units = buildMemberUnits(people, byKey, undefined, true)
    return (
        <div className="flex flex-col items-center gap-2 w-full">
            <div className="flex items-center gap-1.5">
                <span className="text-2xs font-bold text-color-400 uppercase tracking-widest">{label}</span>
                {people.length > 1 && (
                    <span className="px-1.5 py-0.5 rounded-full text-2xs font-bold bg-color-100 text-color-400">{people.length}</span>
                )}
            </div>
            {/* items-stretch - mọi box trong cùng 1 dòng cao bằng nhau (bằng box cao nhất, vd có
                badge linh mục/tu sĩ hoặc dòng tuổi mà box khác không có) - cùng lý do/cách sửa đã áp
                dụng cho hàng "cùng thế hệ" (phản hồi thực tế "cân đối chiều cao, đồng đều cho đẹp"). */}
            <div className="flex flex-wrap justify-center items-stretch gap-2 w-full">
                {units.map((unit) => (
                    <UnitCard
                        key={unit[0].dedup_key}
                        unit={unit}
                        tier={tier}
                        focus={!!focusKey && unit.some((p) => p.dedup_key === focusKey)}
                        onSelect={onSelect}
                        onViewDetail={onViewDetail}
                        t={t}
                    />
                ))}
            </div>
        </div>
    )
}

// Ô ghost dùng khung giống layout thật của cây, tái dùng cho cả trạng thái đang tải (shimmer chạy)
// và trạng thái chưa có dữ liệu (tĩnh, mờ) - để user hình dung được cấu trúc cây sẽ trông ra sao
// thay vì màn hình trắng/spinner trơn không gợi ý gì.
const TreeSkeletonBlock = ({animated, style}) => (
    <div
        className="rounded"
        style={{
            background: 'var(--color-family-100)',
            animation: animated ? 'family-tree-sk 1.3s infinite ease-in-out' : undefined,
            ...style,
        }}
    />
)

const TreeSkeleton = ({animated}) => (
    <div className="flex flex-col items-center gap-3">
        <style>{`@keyframes family-tree-sk{0%{opacity:.45}50%{opacity:.9}100%{opacity:.45}}`}</style>
        {[{cards: 2}, {cards: 1}, {cards: 2}].map(({cards}, i) => (
            <React.Fragment key={i}>
                {i > 0 && <TreeSkeletonBlock animated={false} style={{width: 1, height: 16}}/>}
                <div className="flex items-stretch gap-2" style={{width: 240}}>
                    {Array.from({length: cards}).map((_x, j) => (
                        <div key={j} className="flex-1 p-3 flex flex-col items-center gap-2 rounded-xl border border-family-100 bg-family-50">
                            <TreeSkeletonBlock animated={animated} style={{width: '70%', height: 14}}/>
                            <TreeSkeletonBlock animated={animated} style={{width: '45%', height: 10}}/>
                        </div>
                    ))}
                </div>
            </React.Fragment>
        ))}
    </div>
)

// Lỗi render (JS runtime error) trong Cây Gia Phả/Tâm Điểm trước đây làm TRẮNG TOÀN BỘ TRANG (React
// unmount cả cây component khi 1 lỗi ném ra mà không có ErrorBoundary nào chặn phía trên) - không có
// thông báo gì cho user biết đã xảy ra chuyện gì. Bọc riêng khu vực này (xem chỗ dùng
// <ErrorBoundary>) để lỗi chỉ làm hỏng ĐÚNG phần cây, không kéo sập cả trang, kèm nút tải lại.
const FamilyTreeErrorFallback = () => (
    <Translate>
        {(t) => (
            <div className="flex flex-col items-center justify-center gap-3 py-10 px-6 text-center">
                <IoWarningOutline size={32} className="text-red-400"/>
                <span className="font-bold text-sm text-color-000">{t('something went wrong')}</span>
                <span className="text-xs text-color-400 max-w-xs">{t('family tree render error hint')}</span>
                <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 rounded-lg bg-family-600 hover:bg-family-700 text-white text-sm font-bold cursor-pointer">
                    {t('reload page')}
                </button>
            </div>
        )}
    </Translate>
)

// ─── Toàn Bộ Cây Gia Phả (dạng sơ đồ phả hệ - nhánh xuống theo đúng cấu trúc cha/mẹ→con, KHÔNG
// phải xếp thành hàng theo thế hệ như tầng của cây Tâm Điểm) ────────────────────────────────────
// Riêng biệt hoàn toàn với cây Tâm Điểm (FamilyTreeSubTab) phía dưới - không đổi/đụng gì ở đó, chỉ
// TẬN DỤNG CHUNG data (forest.members, buildMemberUnits, effective_parent_dedup_keys) để vẽ 1 sơ đồ
// nhánh-cây thật (giống mẫu phả đồ truyền thống: gốc trên cùng, chia nhánh xuống theo từng đời) -
// dùng kỹ thuật CSS org-chart kinh điển (ul/li lồng nhau + border tạo đường nối chữ L, không cần
// canvas/SVG/thư viện ngoài) để vẽ đường nối cha/mẹ-con thật, đúng yêu cầu "bỏ râu ria" (không rồng
// phượng/khung viền trang trí như mẫu phả đồ truyền thống - chỉ giữ đúng cấu trúc cây phân nhánh).
//
// Giới hạn độ sâu tối đa PEDIGREE_MAX_DEPTH (đưa thành 1 hằng số cấu hình riêng, dễ chỉnh sau) -
// cây phả hệ có thể sâu vô hạn về lý thuyết, giới hạn để tránh hiệu năng kém tòm nhánh quá dài trên
// mobile; nhánh bị cắt do chạm giới hạn báo bằng 1 chấm nhỏ dưới ô (không phải trang trí - báo hiệu
// còn dữ liệu chưa hiện, bấm vào tên vẫn xem chi tiết được).
// PEDIGREE_MAX_DEPTH/PedigreeBox/PEDIGREE_BOX_WIDTH(_ROTATED) - tách sang functions/pedigreeTree.js
// và components/PedigreeBox.js (dùng chung với renderer SVG thử nghiệm), import ở đầu file.

// unit: [primary, spouse?] (ghép qua buildMemberUnits). visited: Set các COUPLE KEY (coupleKeyOf,
// KHÔNG phải dedup_key của 1 người) dùng CHUNG xuyên suốt cả cây (mutate trực tiếp khi render - chấp
// nhận được vì đây là guard chống lặp vô hạn/hiện trùng, không phải state cần trigger re-render) -
// phòng trường hợp match/dedup tạo vòng lặp. Guard theo COUPLE (không phải theo NGƯỜI) vì 1 người
// có nhiều vợ/chồng (chuyển dòng.. à không, tái hôn) hợp lệ xuất hiện ở NHIỀU unit anh chị em cùng
// cấp (vd Linh + Trúc, Linh + Trúc 2 đứng cạnh nhau) - chặn theo người sẽ làm mất cặp thứ 2.
//
// Ghi chú nhỏ "Hôn nhân N" gắn trên primary khi người này có NHIỀU cuộc hôn nhân (groupSize > 1) - số
// thứ tự ĐÚNG cho TỪNG ô (groupIndex+1), không phải tổng số dùng chung cho mọi ô - mỗi ô tự biết rõ
// mình là cuộc hôn nhân thứ mấy, không cần đoán qua ô khác. ĐÃ THỬ "N đời vợ"/"N đời chồng" (theo
// giới tính), phản hồi thực tế thấy không tử tế/hơi nhạy cảm - đổi qua cách gọi trung tính, không
// phân biệt giới tính (đúng như nhau cho ông nhiều vợ lẫn bà nhiều chồng). Chữ nhạt/nhỏ (xem
// PedigreeBox) - chỉ để nhận ra, không phải nhãn nổi bật.
const pedigreeMarriageNote = (groupSize, groupIndex, t) => (groupSize > 1 ? t('pedigree marriage ordinal', {n: groupIndex + 1}) : null)

// `groupSize`/`groupIndex`: unit này là 1 trong NHIỀU cuộc hôn nhân của cùng 1 primary khi groupSize
// > 1 (xem chỗ gọi - groupUnitsForRender ở dưới, groupIndex = vị trí 0-based trong nhóm đó) - do sắp
// theo năm sinh, các unit cùng primary vốn ĐÃ đứng liền kề nhau sẵn thành nhiều PedigreeNode/`<li>`
// riêng biệt (lặp lại tên primary, KHÔNG gộp thành 1 box - đã thử gộp/đóng khung nét đứt/đánh badge
// số thứ tự trước đây, đều bị phản hồi không ổn, xem lịch sử ở groupUnitsForRender). Thay vào đó
// dùng 2 TÍN HIỆU MÀU nhẹ (xem PedigreeBox) để nhận ra "đây là 1 nhóm" mà KHÔNG đổi cấu trúc `<li>`/
// đường nối đang vẽ (an toàn, không có rủi ro tính toán vị trí bằng tay từng gặp phải với khung nét
// đứt bao quanh nhiều box rời): (1) tô nền đậm hơn 1 cấp cho cả nhóm, (2) RIÊNG primary - lần xuất
// hiện đầu tiên tô như bình thường trong nhóm, các lần lặp lại sau đó (groupIndex > 0, "đã giới thiệu
// rồi") nhạt hơn 1 nấc nữa - kèm ghi chú "N đời vợ/chồng" để khỏi cần đoán qua màu.
const PedigreeNode = ({unit, depth, groupSize = 1, groupIndex = 0, byKey, childrenByEffectiveParent, childrenByCouple, visited, rotateFromDepth, onViewDetail, t}) => {
    const [primary, spouse] = unit
    const unitKey = coupleKeyOf(unit)
    if (!primary) return null

    // Cặp vợ/chồng nối 2 dòng họ khác nhau (1 bên có cha/mẹ ở nhánh A, bên kia có cha/mẹ ở nhánh B -
    // vd Dung con Trần Văn An, lấy Nhi con Lê Việt Dũng) VỀ BẢN CHẤT là 1 đồ thị, không phải cây -
    // chỉ đệ quy con cháu được ở ĐÚNG 1 nhánh (nhánh duyệt TỚI TRƯỚC, không có ý nghĩa ưu tiên gì).
    // Trước đây nhánh duyệt tới SAU return null thẳng - cặp biến mất KHÔNG DẤU VẾT khỏi nhánh đó
    // (bug thực tế đã gặp - vd Trần Văn An mất hẳn 1 người con trên cây dù dữ liệu/link đều đúng).
    // Quyết định cùng user: thà vẽ LẶP LẠI còn hơn mất trắng - nhánh sau vẫn vẽ đủ 2 ô (primary+spouse)
    // nhưng KHÔNG đệ quy xuống con cháu nữa (tránh nhân đôi cả cây con), kèm nhãn nhỏ "elsewhere"
    // (PedigreeBox) báo đây là bản phụ, xem đầy đủ ở nhánh kia.
    const isDuplicateElsewhere = visited.has(unitKey)
    visited.add(unitKey)

    // Con cái CHỈ tính ĐÚNG theo cặp đang render (childrenByCouple, khớp CHÍNH XÁC cả 2 dedup_key)
    // khi có spouse - KHÔNG union con của primary với con của spouse ở MỌI cuộc hôn nhân khác (bug cũ:
    // 2 vợ khác nhau nhưng con của cả 2 bị gộp chung vào bất kỳ unit nào render trước). Chỉ fallback
    // về childrenByEffectiveParent (theo 1 mình primary) khi render solo (không tìm được spouse trong
    // phạm vi đang xem) - khi đó không có cặp để so khớp chính xác.
    const childKeys = isDuplicateElsewhere ? new Set() : new Set(
        spouse
            ? (childrenByCouple[unitKey] || [])
            : (childrenByEffectiveParent[primary.dedup_key] || [])
    )
    const hasMoreChildren = childKeys.size > 0
    const atDepthLimit = depth >= PEDIGREE_MAX_DEPTH
    const childUnits = (!atDepthLimit && hasMoreChildren)
        ? buildMemberUnits(Array.from(childKeys).map((k) => byKey[k]).filter(Boolean), byKey)
        : []
    // rotateFromDepth: null = tắt hẳn; ngược lại xoay chữ 90° từ cấp này trở xuống ("ở các cấp con",
    // không xoay ở gốc để giữ dễ đọc cho tên chính) - so sánh >= chứ không so tuyệt đối vì áp dụng
    // đồng nhất cho MỌI nhánh xuống tới đáy, không chỉ đúng 1 tầng.
    const rotated = rotateFromDepth != null && depth >= rotateFromDepth
    // CHỈ tô nền ở LẦN LẶP LẠI của primary (groupIndex > 0, "đã hiện ở box khác rồi") - lần đầu tiên
    // (groupIndex 0) trông y hệt mọi người khác (nền trắng), vợ/chồng CŨNG luôn nền trắng (luôn là
    // người thật, không phải bản lặp) - phản hồi thực tế "3 cái kia nên trắng, Mai Văn Phần chỉ 1 cái
    // trắng, cái kia màu khác" (không tô cả nhóm nữa, chỉ tô đúng chỗ cần phân biệt).
    const primaryTinted = groupSize > 1 && groupIndex > 0

    return (
        <li>
            <div className="flex items-stretch justify-center gap-1">
                <PedigreeBox
                    person={primary}
                    truncated={atDepthLimit && hasMoreChildren}
                    rotated={rotated}
                    tinted={primaryTinted}
                    elsewhere={isDuplicateElsewhere}
                    marriageNote={pedigreeMarriageNote(groupSize, groupIndex, t)}
                    onViewDetail={onViewDetail}
                    t={t}
                />
                {spouse && (
                    <>
                        <UnitConnector/>
                        <PedigreeBox
                            person={spouse}
                            inLaw
                            rotated={rotated}
                            elsewhere={isDuplicateElsewhere}
                            onViewDetail={onViewDetail}
                            t={t}
                        />
                    </>
                )}
            </div>
            {childUnits.length > 0 && (
                <ul>
                    {/* groupUnitsForRender: >1 unit CÙNG primary (multi-spouse) - render THÀNH NHIỀU
                        PedigreeNode liền kề bình thường (KHÔNG gộp 1 box), chỉ truyền groupSize/
                        groupIndex để tô nền nhận diện cùng 1 nhóm - xem giải thích ở khai báo props
                        phía trên PedigreeNode. React.Fragment (không phải div) để các <li> vẫn là
                        con trực tiếp của <ul> (bắt buộc cho CSS org-chart tính đúng vị trí đường nối). */}
                    {groupUnitsForRender(childUnits).map((group) => (
                        <React.Fragment key={group.map((u) => coupleKeyOf(u)).join('_')}>
                            {group.map((childUnit, idx) => (
                                <PedigreeNode
                                    key={coupleKeyOf(childUnit)}
                                    unit={childUnit}
                                    depth={depth + 1}
                                    groupSize={group.length}
                                    groupIndex={idx}
                                    byKey={byKey}
                                    childrenByEffectiveParent={childrenByEffectiveParent}
                                    childrenByCouple={childrenByCouple}
                                    visited={visited}
                                    rotateFromDepth={rotateFromDepth}
                                    onViewDetail={onViewDetail}
                                    t={t}
                                />
                            ))}
                        </React.Fragment>
                    ))}
                </ul>
            )}
        </li>
    )
}

// Đường nối kiểu sơ đồ tổ chức cổ điển bằng thuần CSS (ul/li + border-top/border-left ghép thành
// hình chữ L) - không cần đo toạ độ/canvas/SVG/thư viện ngoài, tự co giãn theo số con thực tế.
// Kèm luôn rule @media print - "..." (pedigree-no-print) chỉ ẩn lúc IN, "pedigree-print-header"
// chỉ HIỆN lúc in (tiêu đề trang trí cho bản in dịp giỗ/lễ, không hiện khi xem trên màn hình vì đã
// có header riêng ở trên rồi, tránh lặp).
const PEDIGREE_TREE_CSS = `
.pedigree-tree, .pedigree-tree ul, .pedigree-tree li { list-style: none; margin: 0; padding: 0; position: relative; }
.pedigree-tree { display: flex; justify-content: center; width: fit-content; min-width: 100%; }
.pedigree-tree ul { display: flex; padding-top: 20px; }
.pedigree-tree li { display: flex; flex-direction: column; align-items: center; padding: 20px 6px 0 6px; }
.pedigree-tree li::before, .pedigree-tree li::after {
    content: ''; position: absolute; top: 0; right: 50%; width: 50%; height: 20px;
    border-top: 2px solid var(--color-family-300);
}
.pedigree-tree li::after { right: auto; left: 50%; border-left: 2px solid var(--color-family-300); }
.pedigree-tree li:only-child::before, .pedigree-tree li:only-child::after { display: none; }
.pedigree-tree li:only-child { padding-top: 0; }
.pedigree-tree li:first-child::before, .pedigree-tree li:last-child::after { border: 0 none; }
.pedigree-tree li:last-child::before { border-right: 2px solid var(--color-family-300); border-radius: 0 6px 0 0; }
.pedigree-tree li:first-child::after { border-radius: 6px 0 0 0; }
.pedigree-tree ul ul::before {
    content: ''; position: absolute; top: 0; left: 50%; width: 0; height: 20px;
    border-left: 2px solid var(--color-family-300);
}
.pedigree-tree > ul { padding-top: 0; }
.pedigree-tree > ul > li { padding-top: 0; }
.pedigree-tree > ul > li::before, .pedigree-tree > ul > li::after { display: none; }
.pedigree-print-header { display: none; }
@media print {
    body > *:not(#pedigree-print-root) { display: none !important; }
    #pedigree-print-root { position: static !important; overflow: visible !important; height: auto !important; }
    #pedigree-print-root .pedigree-no-print { display: none !important; }
    #pedigree-print-root .pedigree-print-header { display: block !important; }
    #pedigree-print-root .pedigree-scroll-area { overflow: visible !important; height: auto !important; cursor: default !important; }
    #pedigree-print-root .pedigree-zoom-wrap { transform: none !important; }
    @page { size: landscape; margin: 10mm; }
}
`

// Header + canvas kéo-thả-xem (mouse drag pan, touch dùng cuộn chạm sẵn có của trình duyệt) + zoom
// + in - tách hẳn khỏi cây Tâm Điểm, "full màn hình" thật (fixed inset-0, không phải modal 95vw còn
// thấy nền mờ xung quanh) để phù hợp trình chiếu/in dịp giỗ, lễ họ.
const PEDIGREE_ZOOM_MIN = 0.5
const PEDIGREE_ZOOM_MAX = 1.5
const PEDIGREE_ZOOM_STEP = 0.1
// Bật/tắt nút chuyển chế độ SVG thử nghiệm - TẠM TẮT (phản hồi thực tế "svg ko thành công, tạm ẩn
// đi, quay về cách html"), giữ nguyên toàn bộ code renderMode/SvgPedigreeTree phía dưới, không xoá.
const SVG_MODE_ENABLED = false

const FullPedigreeModal = ({forest, onClose, onViewDetail, t}) => {
    const members = forest?.members || []
    const byKey = useMemo(() => _.keyBy(members, 'dedup_key'), [members])
    const childrenByEffectiveParent = useMemo(() => {
        const map = {}
        members.forEach((m) => {
            (m.effective_parent_dedup_keys || []).forEach((pk) => {
                if (!map[pk]) map[pk] = []
                if (!map[pk].includes(m.dedup_key)) map[pk].push(m.dedup_key)
            })
        })
        return map
    }, [members])
    // Con cái theo ĐÚNG CẶP cha+mẹ (key = coupleKeyOf, không phân biệt thứ tự) - CHỈ khớp khi biết
    // đủ CẢ 2 cha/mẹ (effective_parent_dedup_keys dài đúng 2). Cần tách riêng khỏi
    // childrenByEffectiveParent (theo 1 người) vì 1 người có thể có NHIỀU cuộc hôn nhân - union theo
    // 1 người sẽ gộp lẫn con của các cuộc hôn nhân khác nhau vào cùng 1 cặp render (bug đã gặp: 2 vợ
    // khác nhau nhưng con của cả 2 hiện chung dưới 1 cặp).
    const childrenByCouple = useMemo(() => {
        const map = {}
        members.forEach((m) => {
            const keys = m.effective_parent_dedup_keys || []
            if (keys.length !== 2) return
            const key = [...keys].sort().join('|')
            if (!map[key]) map[key] = []
            if (!map[key].includes(m.dedup_key)) map[key].push(m.dedup_key)
        })
        return map
    }, [members])
    // Gốc = người KHÔNG có cha/mẹ nào khác nằm trong rừng này (không suy theo generation - đỡ phụ
    // thuộc thêm 1 nguồn tính toán khác, chỉ cần đúng 1 tín hiệu duy nhất: effective_parent_dedup_keys)
    // VÀ KHÔNG PHẢI vợ/chồng "married-in" của 1 người đã có cha/mẹ (bug đã gặp: 1 người tự lập sổ
    // riêng khi kết hôn - "gia trưởng sổ riêng" - không có cha/mẹ trong rừng nên bị hiểu nhầm là 1
    // gốc tổ tiên độc lập, nổi lên ngang hàng ông bà thay vì đứng cạnh đúng vợ/chồng ở đúng thế hệ).
    // Có thể ra >1 gốc nếu rừng có nhiều nhánh tổ tiên rời rạc (hiếm) - vẽ thành nhiều cây song song,
    // CSS đã tự ẩn đường nối ngang giữa các gốc cấp cao nhất (.pedigree-tree > ul > li).
    // findRootUnits() - tách sang pedigreeTree.js, DÙNG CHUNG với ForestSelector (label nút chọn
    // rừng) + defaultFocusKey (Tâm Điểm) - xem giải thích đầy đủ ở đó.
    const rootUnits = useMemo(() => findRootUnits(members, byKey), [members, byKey])
    // KHÔNG bọc useMemo - visited bị MUTATE trực tiếp lúc render (từng PedigreeNode tự add() mình
    // vào) để chống lặp vô hạn TRONG 1 lượt render. useMemo([forest]) sẽ giữ nguyên đúng 1 Set đã
    // bị add ĐẦY suốt các lần render sau (zoom, bấm xem chi tiết...) vì forest không đổi - lượt
    // render thứ 2 trở đi thấy mọi người đều "đã visited" ngay từ gốc, cây biến mất trắng trơn (bug
    // thực tế đã gặp: bấm zoom hoặc bấm 1 node là mất trắng cây). Phải là 1 Set MỚI mỗi lần hàm
    // component chạy lại - plain literal, không phải giá trị nhớ qua các lần render.
    const visited = new Set()
    const rootLabel = rootUnits.length
        ? rootUnits.map((u) => u.map((p) => p.name).join(' - ')).join(', ')
        : null

    const [zoom, setZoom] = useState(1)
    // Xoay chữ 90° từ cấp 2 trở xuống ("ở các cấp con", chừa gốc/cấp 1 để dễ đọc) - mặc định TẮT,
    // chỉ là công cụ tuỳ chọn khi 1 tầng có quá nhiều anh chị em làm cây tràn ngang quá rộng, không
    // phải hành vi tự động vì đánh đổi khả năng đọc (đặc biệt khi in treo dịp giỗ, lễ họ).
    const [rotateChildren, setRotateChildren] = useState(false)
    // Chế độ vẽ THỬ NGHIỆM song song ('css' = kỹ thuật ul/li đang ổn định, 'svg' = toạ độ tính trước +
    // đường nối SVG thật, xem SvgPedigreeTree.js) - mặc định vẫn 'css' (không đổi hành vi cũ khi mở
    // màn hình), bấm nút mới thấy 'svg'. Đúng yêu cầu "song song, dùng thử, không được thì quay lại
    // cái hiện tại" - KHÔNG xoá/đụng nhánh 'css', chỉ thêm 1 nhánh render khác cạnh nhau.
    const [renderMode, setRenderMode] = useState('css')
    const scrollRef = useRef(null)
    const dragRef = useRef(null)

    // Khoá cuộn trang nền trong lúc xem full màn hình - tránh cuộn "xuyên" ra ngoài gây rối.
    useEffect(() => {
        const original = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => { document.body.style.overflow = original }
    }, [])

    // Phím Esc để thoát - lối thoát dự phòng cho màn hình full-screen, không phụ thuộc hoàn toàn
    // vào việc bấm trúng nút Đóng (đã gặp thực tế nút bị che khuất do lệch z-index với thanh nav).
    useEffect(() => {
        const handleKeyDown = (e) => { if (e.key === 'Escape') onClose() }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Kéo-thả-xem bằng chuột (touch đã có cuộn chạm gốc của trình duyệt, không cần thêm) - "kéo qua
    // kéo về" theo đúng yêu cầu, tiện hơn hẳn chỉ có thanh cuộn khi cây rộng nhiều nhánh.
    useEffect(() => {
        const handleMove = (e) => {
            if (!dragRef.current || !scrollRef.current) return
            scrollRef.current.scrollLeft = dragRef.current.scrollLeft - (e.clientX - dragRef.current.x)
            scrollRef.current.scrollTop = dragRef.current.scrollTop - (e.clientY - dragRef.current.y)
        }
        const handleUp = () => { dragRef.current = null }
        window.addEventListener('mousemove', handleMove)
        window.addEventListener('mouseup', handleUp)
        return () => {
            window.removeEventListener('mousemove', handleMove)
            window.removeEventListener('mouseup', handleUp)
        }
    }, [])
    const handleMouseDown = (e) => {
        if (e.button !== 0 || !scrollRef.current) return
        dragRef.current = {x: e.clientX, y: e.clientY, scrollLeft: scrollRef.current.scrollLeft, scrollTop: scrollRef.current.scrollTop}
    }

    // z-index 1060 - PHẢI cao hơn thanh điều hướng trên cùng (SiderMenu.css, z-index: 1050) để che
    // được hết (đã gặp thực tế: nút Đóng/zoom/in bị thanh nav đè lên, hoàn toàn không bấm được vì
    // z-index cũ 900 thấp hơn 1050). MemberDetailModal (antd Modal/Drawer, portal riêng) vẫn nổi lên
    // trên bình thường - antd tự quản lý z-index động cho modal đang mở, luôn cao hơn giá trị tĩnh
    // ở đây (đã kiểm chứng qua mọi modal khác trong app từ đầu tới giờ, luôn nổi trên SiderMenu).
    //
    // ReactDOM.createPortal ra thẳng document.body - "fixed inset-0" chỉ thật sự bám theo VIEWPORT
    // khi KHÔNG có ancestor nào giữa nó và <body> có transform/filter/perspective (tạo containing
    // block riêng, kinh điển làm fixed "dính" theo khung cha thay vì màn hình) - layout page của
    // app có khả năng có 1 wrapper animation/transition dùng transform ở đâu đó phía trên, portal
    // thẳng ra body loại bỏ hẳn rủi ro này thay vì phải dò từng ancestor.
    return ReactDOM.createPortal(
        <div id="pedigree-print-root" className="family-theme fixed inset-0 flex flex-col" style={{zIndex: 1060, background: 'var(--color-family-50)'}}>
            <style>{PEDIGREE_TREE_CSS}</style>

            {/* Header - ẩn khi in, chỉ để điều khiển trên màn hình */}
            <div className="pedigree-no-print flex items-center justify-between gap-2 px-3 md:px-4 py-2.5 border-b border-family-200 background flex-shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                    <div style={{width: 34, height: 34}} className="flex flex-center rounded-full bg-family-600 flex-shrink-0">
                        <GiFamilyTree size={16} className="text-white"/>
                    </div>
                    <div className="min-w-0">
                        <div className="font-bold text-sm text-color-000 uppercase tracking-wide truncate">{t('full pedigree title')}</div>
                        <div className="text-2xs text-color-400 truncate">{t('pedigree max depth hint', {n: PEDIGREE_MAX_DEPTH})}</div>
                    </div>
                </div>
                <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
                    <div className="flex items-center gap-0.5 bg-color-50 rounded-lg p-0.5">
                        <button
                            type="button"
                            onClick={() => setZoom((z) => Math.max(PEDIGREE_ZOOM_MIN, +(z - PEDIGREE_ZOOM_STEP).toFixed(2)))}
                            className="w-7 h-7 flex flex-center rounded-md hover:bg-color-100 cursor-pointer">
                            <IoRemove size={14} className="text-color-400"/>
                        </button>
                        <span className="text-2xs font-bold text-color-400 w-10 text-center">{Math.round(zoom * 100)}%</span>
                        <button
                            type="button"
                            onClick={() => setZoom((z) => Math.min(PEDIGREE_ZOOM_MAX, +(z + PEDIGREE_ZOOM_STEP).toFixed(2)))}
                            className="w-7 h-7 flex flex-center rounded-md hover:bg-color-100 cursor-pointer">
                            <IoAdd size={14} className="text-color-400"/>
                        </button>
                    </div>
                    {/* Toggle CSS/SVG - THỬ NGHIỆM, xem giải thích ở khai báo renderMode. TẠM ẨN nút
                        bấm (SVG_MODE_ENABLED=false) theo phản hồi thực tế "svg ko thành công" - CHỪA
                        NGUYÊN code renderMode/SvgPedigreeTree lại (không xoá), chỉ không cho bấm vào
                        được nữa - dễ bật lại sau nếu cần quay lại thử tiếp. */}
                    {SVG_MODE_ENABLED && (
                        <button
                            type="button"
                            onClick={() => setRenderMode((m) => m === 'svg' ? 'css' : 'svg')}
                            title={t('pedigree render mode hint')}
                            className={classNames(
                                'px-2.5 md:px-3 py-1.5 flex items-center gap-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-no-wrap',
                                renderMode === 'svg' ? 'bg-family-600 text-white' : 'bg-color-50 text-color-400 hover:bg-color-100'
                            )}>
                            <GiFamilyTree size={14}/>
                            <span className="hidden md:inline">{renderMode === 'svg' ? t('pedigree mode svg') : t('pedigree mode css')}</span>
                        </button>
                    )}
                    {renderMode === 'css' && (
                        <button
                            type="button"
                            onClick={() => setRotateChildren((v) => !v)}
                            title={t('pedigree rotate children hint')}
                            className={classNames(
                                'px-2.5 md:px-3 py-1.5 flex items-center gap-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-no-wrap',
                                rotateChildren ? 'bg-family-600 text-white' : 'bg-color-50 text-color-400 hover:bg-color-100'
                            )}>
                            <IoSwapVerticalOutline size={14}/>
                            <span className="hidden md:inline">{t('pedigree rotate children')}</span>
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => window.print()}
                        className="px-2.5 md:px-3 py-1.5 flex items-center gap-1.5 rounded-lg bg-family-600 hover:opacity-85 text-white text-xs font-semibold cursor-pointer whitespace-no-wrap">
                        <IoPrintOutline size={14}/>
                        <span className="hidden md:inline">{t('print')}</span>
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-8 h-8 flex flex-center rounded-lg hover:bg-color-100 cursor-pointer flex-shrink-0">
                        <IoClose size={20} className="text-color-400"/>
                    </button>
                </div>
            </div>

            {/* Canvas - cuộn/kéo được, zoom qua transform scale */}
            <div
                ref={scrollRef}
                onMouseDown={handleMouseDown}
                className="pedigree-scroll-area flex-1 overflow-auto cursor-grab active:cursor-grabbing select-none"
                style={{background: 'var(--color-family-50)'}}>
                {/* Chỉ hiện khi in - tiêu đề trang trí cho bản treo/in dịp giỗ, lễ họ */}
                <div className="pedigree-print-header text-center pt-6 pb-2">
                    <div className="text-xl font-bold uppercase tracking-widest text-family-700">{t('full pedigree title')}</div>
                    {rootLabel && <div className="text-sm text-color-400 italic mt-1">{t('pedigree root label', {name: rootLabel})}</div>}
                </div>
                <div className="pedigree-zoom-wrap" style={{transform: `scale(${zoom})`, transformOrigin: 'top center', padding: '28px 20px 48px'}}>
                    {renderMode === 'svg' ? (
                        <SvgPedigreeTree
                            rootUnits={rootUnits}
                            byKey={byKey}
                            childrenByEffectiveParent={childrenByEffectiveParent}
                            childrenByCouple={childrenByCouple}
                            onViewDetail={onViewDetail}
                            t={t}
                        />
                    ) : (
                        <div className="pedigree-tree">
                            <ul>
                                {/* Cùng pattern với PedigreeNode's children (Fragment giữ <li> làm con
                                    trực tiếp của <ul>, groupSize/groupIndex tô nền nhận nhóm multi-
                                    marriage) - xem giải thích ở khai báo PedigreeNode. */}
                                {groupUnitsForRender(rootUnits).map((group) => (
                                    <React.Fragment key={group.map((u) => coupleKeyOf(u)).join('_')}>
                                        {group.map((unit, idx) => (
                                            <PedigreeNode
                                                key={coupleKeyOf(unit)}
                                                unit={unit}
                                                depth={1}
                                                groupSize={group.length}
                                                groupIndex={idx}
                                                rotateFromDepth={rotateChildren ? 2 : null}
                                                byKey={byKey}
                                                childrenByEffectiveParent={childrenByEffectiveParent}
                                                childrenByCouple={childrenByCouple}
                                                visited={visited}
                                                onViewDetail={onViewDetail}
                                                t={t}
                                            />
                                        ))}
                                    </React.Fragment>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    )
}

const FamilyTreeSubTab = ({t}) => {
    const {forests, isLoading, login} = useMyFamilyForests()
    const [selected, setSelected] = useState(0)
    const [focusKey, setFocusKey] = useState(null)
    const [detailMemberId, setDetailMemberId] = useState(null)
    const [showFullPedigree, setShowFullPedigree] = useState(false)
    const forest = forests[selected]

    const members = forest?.members || []
    const byKey = useMemo(() => _.keyBy(members, 'dedup_key'), [members])

    // Tâm điểm mặc định = GỐC THẬT của rừng (findRootUnits, cùng nguồn FullPedigreeModal/
    // ForestSelector) - TRƯỚC ĐÂY lấy gia trưởng của forest.books[0] (sổ đầu tiên theo thứ tự mảng
    // BE trả về, KHÔNG liên quan gì tới ai là gốc thật) - sai ngay khi rừng nhiều sổ, tâm điểm rơi
    // vào 1 người "lưng chừng" bất kỳ thay vì đúng gốc, khiến tưởng người đó "chưa có cha/mẹ" trong
    // khi thật ra vợ/chồng họ mới là người còn cha/mẹ để lần lên tiếp (bug thực tế đã gặp). Đổi rừng
    // (hoặc lần đầu tải xong) thì reset tâm điểm về gốc này.
    const defaultFocusKey = useMemo(() => {
        const rootUnit = findRootUnits(members, byKey)[0]
        return rootUnit?.[0]?.dedup_key || members[0]?.dedup_key || null
    }, [members, byKey])

    useEffect(() => { setFocusKey(defaultFocusKey) }, [defaultFocusKey])

    // con của 1 người = mọi member có dedup_key của người đó trong effective_parent_dedup_keys (đã
    // gộp sẵn blood-chain thật + suy theo relationship_type ở BE, không tự tính lại ở đây).
    const childrenByEffectiveParent = useMemo(() => {
        const map = {}
        members.forEach((m) => {
            (m.effective_parent_dedup_keys || []).forEach((pk) => {
                if (!map[pk]) map[pk] = []
                if (!map[pk].includes(m.dedup_key)) map[pk].push(m.dedup_key)
            })
        })
        return map
    }, [members])

    const sortByBirth = (list) => [...list].sort((a, b) => {
        const da = a.birth_date ? dayjs(a.birth_date).valueOf() : Infinity
        const db = b.birth_date ? dayjs(b.birth_date).valueOf() : Infinity
        return da - db
    })

    const focusPerson = byKey[focusKey]
    // Mảng - 1 người có thể nhiều vợ/chồng (nhiều sổ độc lập, xem giải thích ở BE).
    const focusSpouses = (focusPerson?.spouse_dedup_keys || []).map((k) => byKey[k]).filter(Boolean)

    const parents = useMemo(
        () => focusPerson ? sortByBirth((focusPerson.effective_parent_dedup_keys || []).map((k) => byKey[k]).filter(Boolean)) : [],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [focusPerson, byKey]
    )
    const grandparents = useMemo(() => {
        const keys = new Set()
        parents.forEach((p) => (p.effective_parent_dedup_keys || []).forEach((k) => keys.add(k)))
        return sortByBirth(Array.from(keys).map((k) => byKey[k]).filter(Boolean))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parents, byKey])
    // Con TÁCH RIÊNG theo từng cặp vợ/chồng (KHÔNG gộp chung 1 rổ như trước - nhìn như cả nhà ở
    // chung, mất hẳn ý nghĩa phả hệ khi có nhiều cuộc hôn nhân). Với mỗi vợ/chồng, chỉ nhận con có
    // ĐÚNG cặp cha/mẹ này trong effective_parent_dedup_keys (khớp cách FullPedigreeModal đã làm,
    // childrenByCouple) - con chưa rõ vợ/chồng nào (hiếm, chỉ có đúng focus trong danh sách cha/mẹ)
    // rơi vào 1 nhóm riêng "spouse: null" thay vì đoán bừa.
    const childGroups = useMemo(() => {
        if (!focusKey) return []
        const allChildKeys = childrenByEffectiveParent[focusKey] || []
        if (!focusSpouses.length) {
            const list = sortByBirth(allChildKeys.map((k) => byKey[k]).filter(Boolean))
            return list.length ? [{spouse: null, children: list}] : []
        }
        const groups = focusSpouses.map((spouse) => ({spouse, keys: new Set()}))
        const unassignedKeys = new Set()
        allChildKeys.forEach((k) => {
            const child = byKey[k]
            const otherParentKeys = (child?.effective_parent_dedup_keys || []).filter((pk) => pk !== focusKey)
            const matched = groups.find((g) => otherParentKeys.includes(g.spouse.dedup_key))
            if (matched) matched.keys.add(k)
            else unassignedKeys.add(k)
        })
        const result = groups
            .map((g) => ({spouse: g.spouse, children: sortByBirth(Array.from(g.keys).map((kk) => byKey[kk]).filter(Boolean))}))
            .filter((g) => g.children.length > 0)
        if (unassignedKeys.size > 0) {
            result.push({spouse: null, children: sortByBirth(Array.from(unassignedKeys).map((kk) => byKey[kk]).filter(Boolean))})
        }
        return result
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusKey, focusSpouses, childrenByEffectiveParent, byKey])
    const siblings = useMemo(() => {
        const parentKeys = focusPerson?.effective_parent_dedup_keys || []
        if (!parentKeys.length) return []
        const keys = new Set()
        parentKeys.forEach((pk) => (childrenByEffectiveParent[pk] || []).forEach((k) => { if (k !== focusKey) keys.add(k) }))
        return sortByBirth(Array.from(keys).map((k) => byKey[k]).filter(Boolean))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusPerson, focusKey, childrenByEffectiveParent, byKey])
    // TÁCH LẠI theo từng cặp (mergeSpouses mặc định false) - tâm điểm nhiều vợ/chồng ra NHIỀU unit
    // riêng (1 unit/1 cuộc hôn nhân), gom hiển thị lại thành 1 khối duy nhất ở bước render qua
    // groupUnitsForRender (xem SiblingMultiMarriageUnit) - KHÔNG gộp thẳng ở đây bằng mergeSpouses=true
    // vì con cái vẫn cần tra cứu ĐÚNG theo từng cặp riêng (childGroups bên dưới, xem giải thích ở đó).
    const siblingUnits = useMemo(
        // .filter(Boolean) - hook này chạy TRƯỚC early-return bên dưới (rule of hooks), có lượt
        // render focusPerson còn undefined (chưa tải xong forest) - không lọc thì buildMemberUnits
        // crash ngay (đọc .dedup_key trên phần tử undefined).
        () => buildMemberUnits([focusPerson, ...siblings].filter(Boolean), byKey),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [focusPerson, siblings, byKey]
    )

    if (!login) {
        return (
            <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <GiFamilyTree size={40} className="text-color-300 mb-3"/>
                <p className="text-sm text-color-400">{t('login to view collection')}</p>
            </div>
        )
    }
    if ((isLoading && !forests.length) || (forests.length && !focusPerson)) {
        return <TreeSkeleton animated/>
    }
    if (!forests.length) {
        return (
            <div className="flex flex-col gap-4">
                <div className="flex flex-col items-center gap-2 text-color-400">
                    <GiFamilyTree size={28} className="opacity-30"/>
                    <span className="text-sm text-center">{t('no family books found')}</span>
                    <span className="text-xs text-color-300 text-center max-w-xs">{t('family_tree_empty_hint')}</span>
                </div>
                <TreeSkeleton animated={false}/>
            </div>
        )
    }

    const rawMemberCount = forest.members.reduce((sum, m) => sum + m.appearances, 0)
    const matchedCount = rawMemberCount - forest.total_members

    return (
        <div className="flex flex-col gap-3">
            <ForestSelector forests={forests} selected={selected} setSelected={setSelected} t={t}/>

            {forest.is_linked ? (
                <div className="p-3 rounded-xl bg-family-50 text-xs text-family-700">
                    {t('this tree has n linked books', {n: forest.book_ids.length})}
                    {matchedCount > 0 && `  ·  ${t('n members matched across books', {n: matchedCount})}`}
                </div>
            ) : (
                <div className="p-3 rounded-xl bg-color-50 text-xs text-color-400">
                    {t('this book is not linked to any other book yet')}
                </div>
            )}

            {focusKey !== defaultFocusKey && (
                <button
                    type="button"
                    onClick={() => setFocusKey(defaultFocusKey)}
                    className="self-start px-3 py-1.5 flex items-center gap-1.5 rounded-lg bg-color-50 hover:bg-color-100 text-xs font-semibold text-color-400 cursor-pointer">
                    <IoChevronBack size={14}/>
                    {t('back to default focus', {name: byKey[defaultFocusKey]?.name})}
                </button>
            )}

            {/* Block riêng dẫn sang sơ đồ phả hệ ĐẦY ĐỦ (tách hẳn khỏi cây Tâm Điểm phía dưới, xem
                FullPedigreeModal) - tận dụng chung data của rừng đang chọn. */}
            <button
                type="button"
                onClick={() => setShowFullPedigree(true)}
                className="p-3 flex items-center gap-3 rounded-xl border border-family-200 bg-family-50 hover:shadow-md cursor-pointer transition-all text-left">
                <div style={{width: 40, height: 40}} className="flex flex-center rounded-full bg-family-600 flex-shrink-0">
                    <GiFamilyTree size={20} className="text-white"/>
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-color-000">{t('view full pedigree')}</div>
                    <div className="text-xs text-color-400">{t('view full pedigree hint')}</div>
                </div>
                <IoChevronForward size={16} className="text-family-600 flex-shrink-0"/>
            </button>

            <div className="flex flex-col items-center gap-2 w-full overflow-x-auto">
                <TierGroup label={t('family_tree_grandparents_tier')} people={grandparents} tier="grandparent" byKey={byKey} onSelect={setFocusKey} onViewDetail={setDetailMemberId} t={t}/>
                {grandparents.length > 0 && <TierConnector/>}

                <TierGroup label={t('family_tree_parents_tier')} people={parents} tier="parent" byKey={byKey} onSelect={setFocusKey} onViewDetail={setDetailMemberId} t={t}/>
                {parents.length > 0 && <TierConnector/>}

                {/* Anh chị em cùng thế hệ hiển thị chung 1 tầng với tâm điểm - không TierConnector nối
                    với tầng cha mẹ ở trên vì đây là quan hệ NGANG HÀNG (anh chị em/vợ chồng), không
                    phải quan hệ cha-con. Tầng này giờ CHỈ vẽ hàng box ngang hàng (không lồng con bên
                    trong từng cột nữa, xem SiblingMultiMarriageUnit) - người nhiều vợ/chồng gộp về 1
                    khối duy nhất (primary hiện 1 lần, KHÔNG lặp lại thành nhiều box trùng tên như
                    trước), nhờ vậy anh/chị/em không con (vd Hai, Lục) đứng NGANG HÀNG đúng nghĩa với
                    khối đó, không bị đẩy lạc xuống dưới do khối kia cao hơn hẳn (phản hồi thực tế đã
                    gặp). Con của tâm điểm dời hẳn sang 1 TẦNG CON riêng, gộp theo từng cặp cha/mẹ
                    trong CÙNG 1 hàng bên dưới (xem khối childGroups ngay sau đây) - không còn tách
                    "hàng cùng thế hệ"/"hàng con" theo từng cột riêng lẻ nữa. */}
                <div className="flex flex-col items-center gap-1.5 w-full">
                    <div className="flex items-center gap-1.5">
                        <span className="text-2xs font-bold text-color-400 uppercase tracking-widest">{t('siblings same generation')}</span>
                        {siblingUnits.length > 1 && (
                            <span className="px-1.5 py-0.5 rounded-full text-2xs font-bold bg-color-100 text-color-400">
                                {siblingUnits.flat().length}
                            </span>
                        )}
                    </div>
                    {/* items-stretch (không phải items-start) - mọi box trong hàng cao bằng nhau
                        (bằng box cao nhất trong CÙNG 1 dòng flex-wrap), tránh cảnh lệch bậc như
                        Hai/Lục (không có dòng caption tuổi/vai trò) thấp hơn hẳn box cạnh bên - phản
                        hồi thực tế "cho có cùng chiều cao". */}
                    <div className="flex flex-wrap justify-center items-stretch gap-3 w-full">
                        {/* groupUnitsForRender: 1 người nhiều vợ/chồng (multi-spouse) gộp về ĐÚNG 1
                            khối (SiblingMultiMarriageUnit) - không còn phụ thuộc việc các unit cùng
                            primary có đứng liền nhau sẵn trong mảng đầu vào hay không (bản đồ theo
                            key, xem groupUnitsForRender). */}
                        {groupUnitsForRender(siblingUnits).map((group) => (
                            group.length > 1 ? (
                                <SiblingMultiMarriageUnit
                                    key={group.map((u) => coupleKeyOf(u)).join('_')}
                                    primary={group[0][0]}
                                    units={group}
                                    focusKey={focusKey}
                                    onSelect={setFocusKey}
                                    onViewDetail={setDetailMemberId}
                                    t={t}
                                />
                            ) : (
                                <UnitCard
                                    key={coupleKeyOf(group[0])}
                                    unit={group[0]}
                                    tier="sibling"
                                    focus={group[0].some((p) => p.dedup_key === focusKey)}
                                    onSelect={setFocusKey}
                                    onViewDetail={setDetailMemberId}
                                    t={t}
                                />
                            )
                        ))}
                    </div>
                </div>

                {/* Tầng con của tâm điểm - TÁCH RIÊNG khỏi hàng "cùng thế hệ" ở trên (trước đây lồng
                    con vào ngay dưới từng cột vợ/chồng, xem lịch sử SiblingMultiMarriageUnit). Vẫn
                    gộp con theo ĐÚNG từng cặp cha/mẹ (childGroups, tránh gộp lẫn con của các cuộc
                    hôn nhân khác nhau vào 1 rổ chung - xem giải thích ở khai báo childGroups), chỉ
                    khác là các nhóm con giờ đứng CẠNH NHAU trong CÙNG 1 hàng, không tách xa theo cột. */}
                {childGroups.length > 0 && (
                    <>
                        <TierConnector/>
                        <div className="flex flex-col items-center gap-1.5 w-full">
                            <span className="text-2xs font-bold text-color-400 uppercase tracking-widest">{t('family_tree_children_tier')}</span>
                            <div className="flex flex-wrap justify-center items-start gap-5 w-full">
                                {childGroups.map((g) => {
                                    // Ghép từng con với vợ/chồng CỦA RIÊNG con đó (buildMemberUnits đọc
                                    // spouse_dedup_keys của mỗi con, mergeSpouses=true - con nhiều vợ/
                                    // chồng gộp về 1 thẻ, cùng cơ chế TierGroup/orderedMembers đang dùng
                                    // ở nơi khác) - TRƯỚC ĐÂY chỉ render PersonBlock đơn lẻ nên dâu/rể
                                    // của con hoàn toàn vắng mặt ở tầng này, phải bấm hẳn vào con mới
                                    // thấy (qua focusSpouses, chỉ áp dụng cho tâm điểm) - phản hồi thực
                                    // tế đã yêu cầu sửa để thấy ngay không cần bấm thêm.
                                    const childUnits = buildMemberUnits(g.children, byKey, undefined, true)
                                    return (
                                        <div key={g.spouse ? g.spouse.dedup_key : 'solo'} className="flex flex-col items-center gap-2">
                                            <span style={{maxWidth: 220}} className="text-2xs font-semibold text-family-600 text-center">
                                                {t('family_tree_children_of', {names: g.spouse ? [focusPerson.name, g.spouse.name].join(' & ') : focusPerson.name})}
                                            </span>
                                            {/* flex-wrap (không phải CSS Grid như bản trước) - Grid cột
                                                cố định theo TREE_CARD_WIDTH chỉ tính đúng khi MỌI ô là 1
                                                PersonBlock đơn; ghép thêm vợ/chồng (UnitConnector + 2
                                                PersonBlock) làm ô rộng hơn hẳn, vỡ layout cột cố định -
                                                đổi sang flex-wrap + items-stretch, cùng pattern đã dùng ổn
                                                định ở hàng "anh chị em cùng thế hệ" ngay phía trên (chấp
                                                nhận đánh đổi: hàng nhiều dòng có thể lệch chiều cao giữa
                                                các dòng, ít quan trọng hơn việc thấy được dâu/rể). */}
                                            <div className="flex flex-wrap justify-center items-stretch gap-2">
                                                {childUnits.map((unit) => (
                                                    <UnitCard
                                                        key={unit[0].dedup_key}
                                                        unit={unit}
                                                        tier="child"
                                                        focus={unit.some((p) => p.dedup_key === focusKey)}
                                                        onSelect={setFocusKey}
                                                        onViewDetail={setDetailMemberId}
                                                        t={t}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </>
                )}
            </div>

            <div className="h-16"/>
            {detailMemberId && (
                <MemberDetailModal key={detailMemberId} memberId={detailMemberId} onClose={() => setDetailMemberId(null)}/>
            )}
            {showFullPedigree && (
                <FullPedigreeModal forest={forest} onClose={() => setShowFullPedigree(false)} onViewDetail={setDetailMemberId} t={t}/>
            )}
        </div>
    )
}

// ─── Gia Phả & Sổ (tab chính thứ 2) ────────────────────────────────────────

const FAMILY_SUB_TAB_KEY = 'family_books_family_subtab'
const FAMILY_SUB_TAB_QUERY = 'subtab'

const FamilySection = ({t}) => {
    const history = useHistory()
    // ?subtab= trên URL là NGUỒN CHÍNH - đúng yêu cầu "click tab lưu vào URL để back/share link giữ
    // đúng tab" (cùng cơ chế với MainTabBar/?tab= ở FamilyBooks root bên dưới). sessionStorage chỉ
    // còn là FALLBACK khi vào thẳng /family-books không kèm query (giữ hành vi "nhớ lần xem gần
    // nhất" đã có từ trước, không bỏ).
    const subTabParam = useRouteSearchParam(FAMILY_SUB_TAB_QUERY)
    const subTab = Object.values(FAMILY_SUB_TABS).includes(subTabParam)
        ? subTabParam
        : (() => {
            try {
                const saved = sessionStorage.getItem(FAMILY_SUB_TAB_KEY)
                return Object.values(FAMILY_SUB_TABS).includes(saved) ? saved : FAMILY_SUB_TABS.BOOKS
            } catch (_) {
                return FAMILY_SUB_TABS.BOOKS
            }
        })()

    const handleSetSubTab = (tab) => {
        try {
            sessionStorage.setItem(FAMILY_SUB_TAB_KEY, tab)
        } catch (_) {
        }
        const params = getRouteSearchParams()
        params.set(FAMILY_SUB_TAB_QUERY, tab)
        history.push({search: '?' + params.toString()})
    }

    return (
        <div className="w-full px-3 pt-1 flex flex-col gap-4">
            <FamilySubTabBar tab={subTab} setTab={handleSetSubTab} t={t}/>

            {subTab === FAMILY_SUB_TABS.BOOKS && <FamilyBooksSubTab t={t}/>}
            {subTab === FAMILY_SUB_TABS.MEMBERS && <FamilyMembersSubTab t={t}/>}
            {subTab === FAMILY_SUB_TABS.TREE && (
                <ErrorBoundary fallbackComponent={FamilyTreeErrorFallback}>
                    <FamilyTreeSubTab t={t}/>
                </ErrorBoundary>
            )}
            <div className="h-16"/>
        </div>
    )
}

// ─── Trang Chủ (tab chính thứ 1) ────────────────────────────────────────────

// Thẻ "Hồ Sơ Của Tôi" - bấm vào mở DrawDialog (KHÔNG chuyển tab Hồ Sơ toàn trang nữa), nội dung tái
// dùng MyMemberCard (cùng 1 nguồn hiển thị với trước đây, không tự chế bản song song). "Kỷ Niệm"
// KHÔNG còn là tab trong popup này nữa - đã lên thẳng thành tab chính (xem MAIN_TABS/MAIN_TAB_ITEMS).
const MyProfileCard = ({items, selectedIndex, setSelectedIndex, t}) => {
    const item = items[selectedIndex] || items[0]
    const [otherMemberId, setOtherMemberId] = useState(null)
    if (!item) return null
    const age = computeAge(item.birth_date, item.death_date)

    return (
        <div className="flex flex-col gap-2">
            {/* 1 user có thể có >1 bản ghi member_user_id ở nhiều sổ khác nhau (vd vừa là gia trưởng
                sổ mình vừa là con trong sổ ba mẹ - xem comment FamilyBookMemberController::
                getMyFamilyBookMembers()) - TÊN các bản ghi này thường TRÙNG NHAU (cùng 1 người thật),
                nên PHẢI có phụ đề phân biệt sổ nào (source_idcode · tên sổ/giáo xứ), nếu không 2 tab
                nhìn y hệt nhau, tưởng nhầm là bug hiển thị trùng lặp (bug thực tế đã gặp, xem phản
                hồi thực tế) - cùng pattern subtitle đã dùng ở OtherBookChip (MemberDetailModal.js),
                không tự chế cách hiển thị song song. Đổi rounded-full (pill 1 dòng) -> rounded-lg
                (2 dòng) để có chỗ cho phụ đề. */}
            {items.length > 1 && (
                <div className="flex gap-2 overflow-x-auto scrollBarMedium">
                    {items.map((m, i) => {
                        const subtitle = [
                            _.get(m, 'family_book.source_idcode'),
                            _.get(m, 'family_book.title') || _.get(m, 'family_book.parish_name'),
                        ].filter(Boolean).join(' · ')
                        return (
                            <button
                                key={m.id}
                                type="button"
                                onClick={() => setSelectedIndex(i)}
                                className={classNames(
                                    'px-3 py-1.5 flex-shrink-0 flex flex-col items-start gap-0 rounded-lg text-left cursor-pointer transition-all max-w-[220px]',
                                    i === selectedIndex ? 'bg-family-600 text-white' : 'bg-family-50 text-family-700'
                                )}>
                                <span className="text-xs font-semibold truncate max-w-full">{m.name}</span>
                                <span className={classNames(
                                    'text-3xs truncate max-w-full',
                                    i === selectedIndex ? 'text-family-100' : 'text-family-400'
                                )}>
                                    {subtitle || t('untitled')}
                                </span>
                            </button>
                        )
                    })}
                </div>
            )}
            <DrawDialog
                width={480}
                wrapClassName="family-theme"
                title={
                    <div className="flex flex-center gap-2">
                        <IoPersonCircleOutline size={16}/>
                        <span className="font-bold uppercase tracking-wide">{item.name}</span>
                    </div>
                }
                footer={false}
                closable={true}
                component={
                    <div
                        style={{background: 'linear-gradient(135deg, var(--color-family-800) 0%, var(--color-family-600) 100%)'}}
                        className="p-4 flex items-center gap-3 rounded-2xl cursor-pointer">
                        <div style={{width: 48, height: 48}} className="flex flex-center rounded-full bg-white bg-opacity-20 text-white font-bold text-xl flex-shrink-0">
                            {getGivenNameInitial(item.name)}
                        </div>
                        <div className="flex-1 min-w-0">
                            <span className="text-2xs font-semibold text-family-100 uppercase tracking-widest">{t('my profile')}</span>
                            <div className="font-bold text-white truncate">{item.name}</div>
                            <span className="text-xs text-family-100">
                                {[age != null ? `${age} ${t('years old')}` : null, item.occupation].filter(Boolean).join(' · ')}
                            </span>
                        </div>
                        <FamilyIcon size={24} className="text-white opacity-70 flex-shrink-0"/>
                    </div>
                }>
                {(isToggle) => isToggle && (
                    <div className="flex flex-col gap-3 p-0 lg:p-2">
                        <MyMemberCard item={item} onOpenOther={setOtherMemberId} t={t}/>
                        {otherMemberId && (
                            <MemberDetailModal
                                key={otherMemberId}
                                memberId={otherMemberId}
                                onClose={() => setOtherMemberId(null)}
                            />
                        )}
                    </div>
                )}
            </DrawDialog>
        </div>
    )
}

// Chưa đăng nhập - trước chỉ 1 dòng chữ nhỏ ("login to view collection"), giờ nâng thành thẻ mời gọi
// rõ ràng có nút "Đăng nhập" - đúng pattern Link to={staticPaths.login} đã dùng ở SongsPage.js
// (MySection), không tự chế cách điều hướng mới.
const FamilyLoginPromptCard = ({t}) => (
    <div className="p-6 flex flex-col items-center gap-3 text-center rounded-2xl border border-family-100 bg-family-50">
        <div style={{width: 56, height: 56}} className="flex flex-center rounded-2xl bg-family-600 flex-shrink-0">
            <FamilyIcon size={26} className="text-white"/>
        </div>
        <div className="flex flex-col gap-1">
            <span className="font-bold text-base text-color-000">{t('family_login_prompt_title')}</span>
            <span className="text-sm text-color-400 leading-snug">{t('family_login_prompt_description')}</span>
        </div>
        <Link
            to={staticPaths.login}
            className="mt-1 px-4 py-2 rounded-xl bg-family-600 hover:bg-family-700 text-white text-sm font-semibold no-underline cursor-pointer">
            {t('login')}
        </Link>
    </div>
)

// Đã đăng nhập nhưng chưa có sổ gia đình nào (total_family_books === 0) - mời tạo thông tin đầu tiên,
// đúng ghi chú thiết kế: "tạo sổ gia đình đầu tiên, nếu chưa có gia đình thì tạo gia đình của bố mẹ",
// làm block dễ hiểu/dễ trải nghiệm để mời người khác tạo theo. Tái dùng thẳng CreateFamilyBookModal
// (đã có sẵn ở FamilyBooksSubTab bên trên trong cùng file) - không tạo modal/API song song.
const FamilyGetStartedCard = ({onClick, t}) => (
    <button
        type="button"
        onClick={onClick}
        className="p-6 flex flex-col items-center gap-3 text-center rounded-2xl border-2 border-dashed border-family-200 bg-family-50 hover:bg-family-100 cursor-pointer transition-all">
        <div style={{width: 56, height: 56}} className="flex flex-center rounded-2xl bg-family-600 flex-shrink-0">
            <FaPlus size={20} className="text-white"/>
        </div>
        <div className="flex flex-col gap-1">
            <span className="font-bold text-base text-color-000">{t('family_get_started_title')}</span>
            <span className="text-sm text-color-400 leading-snug">{t('family_get_started_description')}</span>
        </div>
        <span className="mt-1 px-4 py-2 rounded-xl bg-family-600 text-white text-sm font-semibold">
            {t('create new family book')}
        </span>
    </button>
)

// ─── Nhập gia phả từ file GEDCOM (.ged) ─────────────────────────────────────
// Click HOẶC kéo-thả file vào khối để import - đúng yêu cầu thiết kế. Gọi callApi() trực tiếp
// (KHÔNG qua useAsyncAction) - ngoại lệ upload file/ảnh đã chốt, vì input là 1 File thô, không
// phải object thường đi qua Redux normalize. 10MB/.ged khớp giới hạn BE
// (FamilyBookController::importGedcomFile).
const GEDCOM_MAX_SIZE = 10 * 1024 * 1024
const GEDCOM_ALLOWED_EXT = ['ged', 'gedcom', 'txt']
const GEDCOM_IMPORT_MSG_KEY = 'gedcom-import-progress'

// Thanh tiến trình MÔ PHỎNG, không phải % thật lấy từ BE - import là 1 API call ĐỒNG BỘ duy nhất
// (upload xong BE mới trả kết quả cuối), queue đang là `sync` (không tự ý đổi theo CLAUDE.md) nên
// không có kênh nào để BE báo "đang xử lý người thứ mấy/tổng bao nhiêu" trong lúc chờ - poll job
// thật không khả thi mà không thêm hạ tầng mới. Chạy nhanh dần rồi CHẬM DẦN, dừng ở 90% chờ sẵn
// (không bao giờ tự chạm 100%) - nhảy thẳng 100% khi có kết quả thật ở handleFile(). Mục đích chỉ
// để trấn an người dùng đang chờ 1 việc có thể mất vài phút (nhiều người/nhiều ảnh), KHÔNG phản ánh
// đúng khối lượng công việc BE đang làm.
const startFakeProgress = (setProgress) => setInterval(() => {
    setProgress((p) => {
        if (p >= 90) return p
        const step = p < 50 ? 8 : p < 75 ? 4 : 1
        return Math.min(90, p + step)
    })
}, 400)

// Reference ỔN ĐỊNH ở module scope (không phụ thuộc state/props nào) - add/removeEventListener bắt
// buộc CÙNG 1 reference mới gỡ đúng, khai trong component body sẽ tạo closure MỚI mỗi render, gỡ
// listener bằng bản khác render là gỡ hụt.
const warnBeforeUnload = (e) => {
    e.preventDefault()
    e.returnValue = '' // Chrome yêu cầu set returnValue để hiện hộp thoại xác nhận rời trang
}

const GedcomImportCard = ({onImported, t}) => {
    const inputRef = useRef(null)
    const [dragActive, setDragActive] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [progress, setProgress] = useState(0)
    const timerRef = useRef(null)

    // Dọn interval + listener nếu component unmount NGAY giữa lúc đang import (vd user điều hướng
    // đi bằng nút back của trình duyệt, KHÔNG bị chặn bởi beforeunload) - tránh setState trên
    // component đã unmount + rò rỉ event listener.
    useEffect(() => () => {
        if (timerRef.current) clearInterval(timerRef.current)
        window.removeEventListener('beforeunload', warnBeforeUnload)
    }, [])

    const handleFile = (file) => {
        if (!file || uploading) return
        const ext = (file.name.split('.').pop() || '').toLowerCase()
        if (!GEDCOM_ALLOWED_EXT.includes(ext)) {
            message.error(t('family_gedcom_import_invalid_type'))
            return
        }
        if (file.size > GEDCOM_MAX_SIZE) {
            message.error(t('family_gedcom_import_too_large'))
            return
        }

        setUploading(true)
        setProgress(0)
        window.addEventListener('beforeunload', warnBeforeUnload)
        timerRef.current = startFakeProgress(setProgress)
        // Toast KHÔNG tự tắt (duration: 0) - phải kiên nhẫn chờ tới khi có kết quả thật mới cập
        // nhật/tắt (cùng key -> message.success/error bên dưới THAY THẾ toast này, không chồng thêm
        // toast mới).
        message.loading({content: t('family_gedcom_import_please_wait'), key: GEDCOM_IMPORT_MSG_KEY, duration: 0})

        callApi({apiInfo: familyBook_importGedcom_Api, values: {file}}).then((result) => {
            clearInterval(timerRef.current)
            window.removeEventListener('beforeunload', warnBeforeUnload)
            setUploading(false)

            if (!result) { // request bị cancel (vd unmount giữa chừng) - im lặng, không phải lỗi thật
                message.destroy(GEDCOM_IMPORT_MSG_KEY)
                setProgress(0)
                return
            }
            if (result.error) {
                setProgress(0)
                message.error({
                    content: _.get(result, 'errorMessages.error') || t('family_gedcom_import_error_generic'),
                    key: GEDCOM_IMPORT_MSG_KEY,
                })
                return
            }

            setProgress(100)
            const stats = result.result || {}
            message.success({
                content: t('family_gedcom_import_success', {
                    families: stats.families_created ?? 0,
                    members: (stats.members_created ?? 0) + (stats.members_updated ?? 0),
                    links: stats.links_created ?? 0,
                }),
                key: GEDCOM_IMPORT_MSG_KEY,
                duration: 5,
            })
            onImported && onImported()
            setTimeout(() => setProgress(0), 1500) // giữ thanh 100% hiện 1 chút rồi mới thu gọn lại UI mặc định
        })
    }

    return (
        <div
            onClick={() => !uploading && inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); if (!uploading) setDragActive(true) }}
            onDragLeave={() => setDragActive(false)}
            onDrop={(e) => {
                e.preventDefault()
                setDragActive(false)
                handleFile(_.get(e, 'dataTransfer.files.0'))
            }}
            className={classNames(
                'p-5 flex flex-col items-center gap-1.5 text-center rounded-2xl border-2 border-dashed transition-all',
                uploading ? 'cursor-wait' : 'cursor-pointer',
                dragActive ? 'border-family-500 bg-family-100' : 'border-family-200 bg-family-50 hover:bg-family-100'
            )}>
            <input
                ref={inputRef}
                type="file"
                accept=".ged,.gedcom,.txt"
                className="hidden"
                disabled={uploading}
                onChange={(e) => {
                    handleFile(_.get(e, 'target.files.0'))
                    e.target.value = null // cho phép chọn lại đúng file đó ở lần sau
                }}
            />
            <div style={{width: 48, height: 48}} className="flex flex-center rounded-2xl bg-family-600 flex-shrink-0">
                {uploading
                    ? <LoadingOutlined className="text-white" style={{fontSize: 20}}/>
                    : <IoCloudUploadOutline size={22} className="text-white"/>}
            </div>
            <span className="font-bold text-sm text-color-000">
                {uploading ? t('family_gedcom_import_uploading') : t('family_gedcom_import_title')}
            </span>
            {uploading ? (
                <div className="w-full max-w-xs flex flex-col gap-1 mt-1">
                    <Progress percent={progress} status="active" showInfo={false} strokeColor="var(--color-family-600)"/>
                    {/* Nhắc lại rõ ràng "đang xử lý, đừng rời trang" ngay dưới thanh tiến trình - toast
                        message.loading() ở góc màn hình dễ bị lướt qua/bỏ lỡ trên mobile, cần 1 chỗ
                        LUÔN nhìn thấy trong lúc chờ. */}
                    <span className="text-2xs text-color-400 leading-snug">{t('family_gedcom_import_please_wait')}</span>
                </div>
            ) : (
                <>
                    <span className="text-xs text-color-400">{t('family_gedcom_import_subtitle')}</span>
                    <span className="text-2xs text-color-300 leading-snug max-w-xs">{t('family_gedcom_import_hint')}</span>
                    <span className="mt-1 px-2 py-0.5 rounded-full text-2xs font-semibold bg-color-100 text-color-400">
                        {t('family_gedcom_import_limit')}
                    </span>
                </>
            )}
        </div>
    )
}

// `stats`/`hasNoFamilyBooks`/`refreshCount`/`onRefresh` nhận từ root (FamilyBooks) - dashboard giờ
// fetch 1 lần ở đó để dùng chung với badge số trên MainTabBar (xem ghi chú ở MainTabBar), HomeSection
// không tự fetch/lưu refreshCount riêng nữa để tránh 2 nguồn refresh lệch nhau.
const HomeSection = ({t, goToFamilyTab, moments, hasNoFamilyBooks, refreshCount, onRefresh}) => {
    const login = useContext(LoginContext)
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [detailMemberId, setDetailMemberId] = useState(null)
    const [showCreate, setShowCreate] = useState(false)

    const {response: profileResp} = useAsync({apiInfo: familyBookMember_getMine_Api, deps: [login?.id, refreshCount]})
    const profileItems = _.get(profileResp, 'data.data', _.get(profileResp, 'data', []))

    const selectedMember = profileItems[selectedIndex]
    const addRelativeBookId = _.get(selectedMember, 'family_book.id')

    return (
        <div className="w-full px-3 pt-1 flex flex-col gap-4">
            {!login ? (
                <FamilyLoginPromptCard t={t}/>
            ) : (
                <>
                    {profileItems?.length > 0 && (
                        <MyProfileCard items={profileItems} selectedIndex={selectedIndex} setSelectedIndex={setSelectedIndex} t={t}/>
                    )}

                    <button
                        type="button"
                        onClick={goToFamilyTab}
                        className="p-4 flex items-center justify-between gap-3 rounded-2xl border border-family-200 bg-family-50 hover:bg-family-100 cursor-pointer transition-all">
                        <div className="flex items-center gap-3">
                            <div style={{width: 44, height: 44}} className="flex flex-center rounded-xl bg-family-600 flex-shrink-0">
                                <FamilyIcon size={22} className="text-white"/>
                            </div>
                            <div className="flex flex-col text-left">
                                <span className="font-bold text-sm text-color-000">{t('view my family books')}</span>
                                <span className="text-xs text-color-400">{t('family_home_cta_subtitle')}</span>
                            </div>
                        </div>
                        <IoChevronForward size={18} className="text-family-500 flex-shrink-0"/>
                    </button>

                    <GedcomImportCard onImported={onRefresh} t={t}/>

                    {hasNoFamilyBooks && (
                        <FamilyGetStartedCard onClick={() => setShowCreate(true)} t={t}/>
                    )}

                    {addRelativeBookId && (
                        <Link
                            to={getLinkToDetail(_.get(selectedMember, 'family_book'))}
                            className="p-3 flex items-center gap-2 rounded-xl bg-green-50 border border-green-100 no-underline">
                            <FaPlus size={13} className="text-green-600 flex-shrink-0"/>
                            <span className="text-sm font-semibold text-green-700">{t('add relative')}</span>
                        </Link>
                    )}

                    {moments.length > 0 && (
                        <div className="flex flex-col gap-2">
                            <span className="font-bold text-xs text-color-400 uppercase tracking-widest">
                                {t('upcoming moments')}
                            </span>
                            {moments.map((m, i) => (
                                <MomentRow key={i} item={m} onClick={(item) => setDetailMemberId(item.member_id)} t={t}/>
                            ))}
                        </div>
                    )}
                </>
            )}
            <div className="h-16"/>

            {detailMemberId && (
                <MemberDetailModal
                    key={detailMemberId}
                    memberId={detailMemberId}
                    onClose={() => setDetailMemberId(null)}
                />
            )}

            {/* Sau khi lập sổ xong -> refetch dashboard/profile + đưa thẳng qua tab "Gia Phả & Sổ" để
                xem ngay sổ vừa tạo, tiếp nối đúng ý "mời gọi click vào" của block phía trên. */}
            <CreateFamilyBookModal
                visible={showCreate}
                onClose={() => setShowCreate(false)}
                onSuccess={() => {
                    setShowCreate(false)
                    onRefresh()
                    goToFamilyTab()
                }}
                t={t}
            />
        </div>
    )
}

// ─── Hồ Sơ (tab chính thứ 3) - thông tin bản thân trong gia phả ────────────
// Dùng chung InfoField/SacramentSlot/OtherBookChip với MemberDetailModal (không tự chế format
// song song) - card ở đây hiển thị đủ như member detail vì đây chính là hồ sơ của bản thân.

const MyMemberCard = ({item, onOpenOther, t}) => {
    const familyBook = _.get(item, 'family_book')
    const sacraments = _.get(item, 'sacraments', [])
    const others = _.get(item, 'others', [])
    const age = computeAge(item.birth_date, item.death_date)
    // Đã mất -> kèm dấu ✝ ngay sau số tuổi (tuổi lúc mất) - quy ước gia phả chuẩn, thay chữ "Đã mất".
    const birthDisplay = item.birth_date
        ? `${age} ${t('years old')}${item.death_date ? ' ✝' : ''} (${dayjs(item.birth_date).format('DD/MM/YYYY')})`
        : null

    return (
        <div className="p-4 flex flex-col gap-3 background border border-color-50 shadow-sm rounded-2xl">
            <div className="flex items-center gap-3">
                <div style={{width: 48, height: 48}} className="flex flex-center rounded-full bg-family-100 text-family-700 font-bold text-lg flex-shrink-0">
                    {getGivenNameInitial(item.name)}
                </div>
                <div className="flex-1 min-w-0">
                    {/* Icon giới tính SAU tên - đồng nhất với các nơi khác */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                        {item.patron_saint_name && (
                            <span className="font-bold text-base text-family-600 leading-snug truncate">{item.patron_saint_name}</span>
                        )}
                        <span className="font-bold text-base text-color-000 leading-snug truncate">{item.name}</span>
                        <GenderIcon gender={item.gender} size={16} className="text-color-300 flex-shrink-0"/>
                        {item.is_priest && <PriestBadge/>}
                        {item.is_religious && <ReligiousBadge/>}
                    </div>
                </div>
            </div>

            <div className="p-3 grid grid-cols-2 gap-3 bg-family-50 border border-family-100 rounded-xl">
                <InfoField label={t('age / date of birth')} value={birthDisplay} t={t}/>
                <InfoField label={t('occupation')} value={item.occupation} t={t}/>
                <InfoField label={t('current address')} value={item.current_address} span t={t}/>
                <InfoField label={t('parish')} value={familyBook?.parish_name} span t={t}/>
                <ContactField
                    label={t('contact email')} value={item.contact_email} href={`mailto:${item.contact_email}`}
                    hidden={item.can_manage_contact === false} t={t}
                />
                <ContactField
                    label={t('contact phone')} value={item.contact_phone} href={`tel:${item.contact_phone}`}
                    hidden={item.can_manage_contact === false} t={t}
                />
            </div>

            {familyBook?.title && (
                <Link
                    to={getLinkToDetail(familyBook)}
                    className="px-3 py-2 flex items-center justify-between rounded-lg bg-family-50 hover:bg-family-100 no-underline transition-all">
                    <span className="text-xs font-semibold text-family-700 truncate">{familyBook.title}</span>
                    <IoChevronForward size={14} className="text-family-500 flex-shrink-0"/>
                </Link>
            )}

            <RelativesCard item={item} t={t}/>

            {sacraments.length > 0 && (
                <div className="flex flex-col gap-2">
                    <span className="text-2xs font-bold text-color-400 uppercase tracking-widest">✝ {t('sacraments received')}</span>
                    <div className="grid grid-cols-2 gap-2">
                        {ALL_SACRAMENT_TYPES.map((type) => (
                            <SacramentSlot key={type} type={type} entry={sacraments.find((s) => s.sacrament_type === type)} t={t}/>
                        ))}
                    </div>
                </div>
            )}

            {others.length > 0 && (
                <div className="flex flex-col gap-2">
                    <span className="text-2xs font-bold text-color-400 uppercase tracking-widest">{t('also appears in other family books')}</span>
                    <div className="flex flex-wrap gap-1.5">
                        {others.map((o) => (
                            <OtherBookChip key={o.id} item={o} onClick={() => onOpenOther && onOpenOther(o.id)} t={t}/>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

// Tab chính thứ 3 "Kỷ Niệm" - MomentsPanel (list đầy đủ + filter/search + action gửi thông báo),
// bọc padding/spacer đồng nhất với 2 tab kia (FamilySection/HomeSection).
const MomentsSection = ({t}) => (
    <div className="w-full px-3 pt-1 flex flex-col gap-3">
        <MomentsPanel t={t}/>
        <div className="h-16"/>
    </div>
)

// ─── Root container ─────────────────────────────────────────────────────────

const MAIN_TAB_QUERY = 'tab'

const FamilyBooks = ({Wrapper = 'div'}) => {
    const t = useTranslate()
    const history = useHistory()
    const login = useContext(LoginContext)
    // ?tab= trên URL là NGUỒN CHÍNH cho tab chính (Trang Chủ/Gia Phả & Sổ/Kỷ niệm) - đúng yêu cầu
    // "click tab lưu vào URL để back/share link giữ đúng tab" (cùng cơ chế ?subtab= ở FamilySection
    // bên trên). Không có query (vào thẳng /family-books) -> mặc định Trang Chủ như hành vi cũ.
    const tabParam = useRouteSearchParam(MAIN_TAB_QUERY)
    const tab = Object.values(MAIN_TABS).includes(tabParam) ? tabParam : MAIN_TABS.HOME

    const setTab = useCallback((newTab) => {
        const params = getRouteSearchParams()
        params.set(MAIN_TAB_QUERY, newTab)
        history.push({search: '?' + params.toString()})
    }, [history])

    // Dashboard fetch ở ROOT (trước đây nằm trong HomeSection) - `stats` giờ cần cho cả badge số trên
    // MainTabBar (luôn hiện, mọi tab) lẫn nội dung HomeSection, nên chỉ fetch 1 lần ở đây rồi truyền
    // xuống, tránh gọi API trùng lặp. refreshCount cũng lên theo để GedcomImportCard/CreateFamilyBookModal
    // trong HomeSection bump lên là badge cập nhật ngay, không lệch dữ liệu với nội dung tab.
    const [refreshCount, setRefreshCount] = useState(0)
    const onRefresh = useCallback(() => setRefreshCount((c) => c + 1), [])
    const {response: dashboardResp, isLoading: dashboardLoading} = useAsync({apiInfo: familyBook_getMyFamilyDashboard_Api, deps: [login?.id, refreshCount]})
    const stats = _.get(dashboardResp, 'data.stats', {})
    const moments = _.get(dashboardResp, 'data.upcoming_moments', [])
    // Chỉ coi là "chưa có sổ nào" khi dashboard đã tải xong - tránh chớp nhoáng hiện block mời tạo
    // rồi biến mất ngay khi total_family_books thật > 0 nhưng chưa kịp fetch xong.
    const hasNoFamilyBooks = !dashboardLoading && dashboardResp && (stats.total_family_books ?? 0) === 0

    return (
        <Wrapper className="family-theme flex flex-col min-h-screen background-50">
            <FamilyBooksHeader/>

            <div className="flex-1 w-full max-w-2xl mx-auto flex flex-col">
                <MainTabBar tab={tab} setTab={setTab} stats={stats}/>

                {tab === MAIN_TABS.HOME && (
                    <HomeSection
                        t={t}
                        goToFamilyTab={() => setTab(MAIN_TABS.FAMILY)}
                        moments={moments}
                        hasNoFamilyBooks={hasNoFamilyBooks}
                        refreshCount={refreshCount}
                        onRefresh={onRefresh}
                    />
                )}
                {tab === MAIN_TABS.FAMILY && <FamilySection t={t}/>}
                {tab === MAIN_TABS.MOMENTS && <MomentsSection t={t}/>}
            </div>
        </Wrapper>
    )
}

export default FamilyBooks
